import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ChevronRight,
  Atom,
  Pill,
  Activity,
  Layers,
  Shield,
  FlaskConical,
  Loader2,
  ExternalLink,
  X,
  Beaker,
  RotateCw,
  Calculator,
  Syringe,
  Microscope,
  Search,
  Crosshair,
  Copy,
  FileDown,
  Check,
  Clock,
  Database,
} from 'lucide-react';
import {
  RadarChart as RadarChartBase,
  PolarGrid as PolarGridBase,
  PolarAngleAxis as PolarAngleAxisBase,
  PolarRadiusAxis as PolarRadiusAxisBase,
  Radar as RadarBase,
  ResponsiveContainer as ResponsiveContainerBase,
} from 'recharts';

import * as $3Dmol from '3dmol';
import apiClient from '../../../api/client';
import { useChemistryStore } from '../../../stores/chemistryStore';
import { useNavigationStore } from '../../../stores/navigationStore';
import { getMoleculeName } from '../MoleculeGrid';
import SaveResultsWizard from '../SaveResultsWizard';

// Recharts 3.x types don't align with React 18 JSX — cast to bypass
const RChart = RadarChartBase as any;
const PGrid = PolarGridBase as any;
const PAngle = PolarAngleAxisBase as any;
const PRadius = PolarRadiusAxisBase as any;
const RRadar = RadarBase as any;
const RContainer = ResponsiveContainerBase as any;

// ---------------------------------------------------------------------------
// Inline 3D Molblock Viewer (compact, for conformer results)
// ---------------------------------------------------------------------------

const MolblockViewer: React.FC<{ molblock: string }> = ({ molblock }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !molblock) return;

    // Small delay to ensure container is rendered with dimensions
    const timer = setTimeout(() => {
      if (!containerRef.current) return;
      if (viewerRef.current) {
        viewerRef.current.removeAllModels();
      } else {
        viewerRef.current = $3Dmol.createViewer(containerRef.current, {
          backgroundColor: '#1a1d22',
          antialias: true,
        });
      }
      viewerRef.current.addModel(molblock, 'sdf');
      viewerRef.current.setStyle({}, {
        stick: { radius: 0.12, colorscheme: 'Jmol' },
        sphere: { scale: 0.25, colorscheme: 'Jmol' },
      });
      viewerRef.current.zoomTo();
      viewerRef.current.render();
    }, 50);

    return () => clearTimeout(timer);
  }, [molblock]);

  const toggleSpin = () => {
    if (!viewerRef.current) return;
    if (spinning) {
      viewerRef.current.spin(false);
    } else {
      viewerRef.current.spin('y', 1);
    }
    setSpinning(!spinning);
  };

  return (
    <div className="relative">
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: 200,
          borderRadius: 6,
          border: '1px solid var(--card-border)',
          overflow: 'hidden',
        }}
      />
      <button
        onClick={toggleSpin}
        className="absolute top-1.5 right-1.5 p-1 rounded"
        style={{
          backgroundColor: spinning ? 'rgba(13, 148, 136, 0.3)' : 'rgba(0,0,0,0.4)',
          color: spinning ? '#5eead4' : '#a7abb6',
          border: 'none',
          cursor: 'pointer',
        }}
        title={spinning ? 'Stop rotation' : 'Rotate'}
      >
        <RotateCw size={12} />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Docking Pose Viewer (3Dmol.js with PDBQT poses)
// ---------------------------------------------------------------------------

const DockingPoseViewer: React.FC<{ poseData: string; bestAffinity: number; receptor: string }> = ({ poseData, bestAffinity, receptor }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const [spinning, setSpinning] = useState(false);
  const [currentMode, setCurrentMode] = useState(1);

  const modeCount = useMemo(() => {
    return (poseData.match(/^MODEL\s+/gm) || []).length;
  }, [poseData]);

  useEffect(() => {
    if (!containerRef.current || !poseData) return;

    const timer = setTimeout(() => {
      if (!containerRef.current) return;
      if (viewerRef.current) {
        viewerRef.current.removeAllModels();
      } else {
        viewerRef.current = $3Dmol.createViewer(containerRef.current, {
          backgroundColor: '#1a1d22',
          antialias: true,
        });
      }

      // Extract the selected model from multi-model PDBQT
      const models = poseData.split(/(?=^MODEL\s+)/m);
      const selectedModel = models[currentMode] || models[1] || poseData;

      viewerRef.current.addModel(selectedModel, 'pdb');
      viewerRef.current.setStyle({}, {
        stick: { radius: 0.14, colorscheme: 'Jmol' },
        sphere: { scale: 0.25, colorscheme: 'Jmol' },
      });
      viewerRef.current.zoomTo();
      viewerRef.current.render();
    }, 50);

    return () => clearTimeout(timer);
  }, [poseData, currentMode]);

  const toggleSpin = () => {
    if (!viewerRef.current) return;
    viewerRef.current.spin(spinning ? false : 'y');
    setSpinning(!spinning);
  };

  return (
    <div style={{ marginTop: 4 }}>
      <div className="flex items-center justify-between px-1 mb-1">
        <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#0D9488' }}>
          Docked Pose — {receptor}
        </span>
        <span className="text-[10px] font-mono" style={{ color: bestAffinity < -7 ? '#16a34a' : bestAffinity < -5 ? '#d97706' : '#dc2626' }}>
          {bestAffinity.toFixed(1)} kcal/mol
        </span>
      </div>
      <div className="relative">
        <div
          ref={containerRef}
          style={{
            width: '100%', height: 200, borderRadius: 6,
            border: '1px solid var(--card-border)', overflow: 'hidden',
          }}
        />
        <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
          {modeCount > 1 && (
            <select
              value={currentMode}
              onChange={(e) => setCurrentMode(Number(e.target.value))}
              className="text-[9px] rounded px-1 py-0.5"
              style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: '#a7abb6', border: 'none', outline: 'none' }}
              onClick={(e) => e.stopPropagation()}
            >
              {Array.from({ length: Math.min(modeCount, 9) }, (_, i) => (
                <option key={i + 1} value={i + 1}>Mode {i + 1}</option>
              ))}
            </select>
          )}
          <button
            onClick={toggleSpin}
            className="p-1 rounded"
            style={{
              backgroundColor: spinning ? 'rgba(13, 148, 136, 0.3)' : 'rgba(0,0,0,0.4)',
              color: spinning ? '#5eead4' : '#a7abb6',
              border: 'none', cursor: 'pointer',
            }}
            title={spinning ? 'Stop rotation' : 'Rotate'}
          >
            <RotateCw size={12} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DataProfile {
  structure: boolean;
  bioactivity_data: boolean;
  ic50: boolean;
  ec50: boolean;
  ki: boolean;
  km_vmax: boolean;
  spr_data: boolean;
  target_associations: boolean;
  reactions: boolean;
  patent_matches: boolean;
  assay_results: boolean;
  pk_data: boolean;
  metabolism_data: boolean;
  docking_results: boolean;
  conformers: boolean;
  permeability_data: boolean;
  solubility_data: boolean;
  plasma_binding: boolean;
  protein_sequence: boolean;
  genomic_data: boolean;
  antibody_sequence: boolean;
  entity_type?: string;
}

interface Interpretation { text: string; color: '#16a34a' | '#d97706' | '#dc2626' }

type ToolAction =
  | { type: 'api'; run: (smiles: string, signal: AbortSignal) => Promise<any>; format: (data: any) => ResultRow[]; interpret?: (data: any) => Interpretation }
  | { type: 'navigate'; path: string; hubPort?: number; hubSlug?: string }
  | { type: 'calcs-inline' }
  | { type: 'name-lookup-inline' }
  | { type: 'mpo-inline' };

interface ResultRow {
  label: string;
  value: string;
  color?: string;
  /** If set, this row carries a 3D molblock for inline viewer */
  molblock?: string;
}

interface WorkbenchTool {
  name: string;
  description: string;
  engine: string;
  tooltip: string;
  showWhen: (p: DataProfile) => boolean;
  action: ToolAction;
}

interface ToolSection {
  id: string;
  label: string;
  icon: React.ReactNode;
  tools: WorkbenchTool[];
  showWhen?: (p: DataProfile) => boolean;
}

// ---------------------------------------------------------------------------
// Result formatters
// ---------------------------------------------------------------------------

const fmtConformers = (d: any): ResultRow[] => {
  const confs = d.conformers || [];
  const rows: ResultRow[] = [
    { label: 'Conformers', value: `${d.num_confs} generated` },
    { label: 'Force field', value: d.forcefield || d.method },
  ];
  confs.slice(0, 5).forEach((c: any, i: number) => {
    rows.push({
      label: `#${i + 1} energy`,
      value: `${c.energy_kcal_mol?.toFixed(2)} kcal/mol`,
    });
  });
  // Attach best conformer molblock for 3D viewer
  if (confs.length > 0 && confs[0].molblock) {
    rows.push({
      label: '3D Structure',
      value: 'Lowest-energy conformer',
      molblock: confs[0].molblock,
    });
  }
  return rows;
};

const fmtHomoLumo = (d: any): ResultRow[] => [
  { label: 'HOMO (E\u2095)', value: `${d.homo_hartree?.toFixed(4)} E\u2095` },
  { label: 'LUMO (E\u2095)', value: `${d.lumo_hartree?.toFixed(4)} E\u2095` },
  { label: 'Gap (eV)', value: `${d.gap_ev?.toFixed(2)} eV`, color: d.gap_ev < 3 ? '#d97706' : undefined },
  { label: 'Electrons', value: String(d.nelec) },
];

const fmtIupac = (d: any): ResultRow[] => [
  { label: 'IUPAC Name', value: d.iupac || d.name || '—' },
  ...(d.canonical_smiles ? [{ label: 'Canonical SMILES', value: d.canonical_smiles }] : []),
];

const fmtProperties = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  if (d.formula != null) rows.push({ label: 'Formula', value: d.formula });
  if (d.mw != null) rows.push({ label: 'MW', value: d.mw.toFixed(2) + ' Da' });
  if (d.logp != null) rows.push({ label: 'LogP', value: d.logp.toFixed(2), color: d.logp > 5 ? '#dc2626' : undefined });
  if (d.tpsa != null) rows.push({ label: 'TPSA', value: d.tpsa.toFixed(1) + ' \u00c5\u00b2' });
  if (d.hba != null) rows.push({ label: 'HBA', value: d.hba + ' (count)' });
  if (d.hbd != null) rows.push({ label: 'HBD', value: d.hbd + ' (count)' });
  if (d.rotatable_bonds != null) rows.push({ label: 'Rotatable Bonds', value: d.rotatable_bonds + ' (count)' });
  if (d.aromatic_rings != null) rows.push({ label: 'Aromatic Rings', value: String(d.aromatic_rings) });
  if (d.ring_count != null) rows.push({ label: 'Total Rings', value: String(d.ring_count) });
  if (d.heavy_atoms != null) rows.push({ label: 'Heavy Atoms', value: d.heavy_atoms + ' (count)' });
  if (d.formal_charge != null) {
    const fc = d.formal_charge;
    const fcStr = fc > 0 ? `+${fc}` : fc < 0 ? String(fc) : '0';
    rows.push({ label: 'Formal Charge', value: fcStr, color: fc !== 0 ? '#d97706' : undefined });
  }
  if (d.fsp3 != null) rows.push({ label: 'Fsp3', value: d.fsp3.toFixed(3) });
  if (d.molar_refractivity != null) rows.push({ label: 'Molar Refractivity', value: d.molar_refractivity.toFixed(2) + ' cm\u00b3/mol' });
  return rows;
};

const fmtLipinski = (d: any): ResultRow[] => {
  let violations = 0;
  const rows: ResultRow[] = [];
  const check = (label: string, val: number | undefined, limit: number, op: '>' | '<') => {
    if (val == null) return;
    const fail = op === '>' ? val > limit : val < limit;
    if (fail) violations++;
    rows.push({ label, value: `${val} ${fail ? '✗' : '✓'}`, color: fail ? '#dc2626' : '#16a34a' });
  };
  check('MW ≤ 500', d.mw, 500, '>');
  check('LogP ≤ 5', d.logp, 5, '>');
  check('HBD ≤ 5', d.hbd, 5, '>');
  check('HBA ≤ 10', d.hba, 10, '>');
  if (d.tpsa != null) {
    const fail = d.tpsa > 140;
    if (fail) violations++;
    rows.push({ label: 'TPSA ≤ 140', value: `${d.tpsa.toFixed(1)} ${fail ? '✗' : '✓'}`, color: fail ? '#dc2626' : '#16a34a' });
  }
  rows.unshift({ label: 'Violations', value: String(violations), color: violations === 0 ? '#16a34a' : violations <= 1 ? '#d97706' : '#dc2626' });
  return rows;
};

const fmtAdmet = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  // Druglikeness verdict
  rows.push({ label: 'Drug-like', value: d.druglike ? 'Yes' : 'No', color: d.druglike ? '#16a34a' : '#dc2626' });
  // Absorption
  rows.push({ label: 'GI Absorption', value: d.gi_absorption, color: d.gi_absorption === 'High' ? '#16a34a' : '#d97706' });
  rows.push({ label: 'Bioavailability Score', value: d.bioavailability_score != null ? d.bioavailability_score.toFixed(2) : '—' });
  // Distribution
  rows.push({ label: 'BBB Permeant', value: d.bbb_permeant, color: d.bbb_permeant === 'Yes' ? '#16a34a' : undefined });
  // Solubility
  rows.push({ label: 'Solubility', value: `${d.esol_class} (LogS ${d.esol_logs} mol/L)`, color: d.esol_logs >= -3 ? '#16a34a' : d.esol_logs >= -4 ? '#d97706' : '#dc2626' });
  // Lipinski / Veber
  rows.push({ label: 'Lipinski', value: d.lipinski_violations === 0 ? 'Pass (0 violations)' : `${d.lipinski_violations} violation${d.lipinski_violations > 1 ? 's' : ''}`, color: d.lipinski_violations === 0 ? '#16a34a' : d.lipinski_violations <= 1 ? '#d97706' : '#dc2626' });
  rows.push({ label: 'Veber', value: d.veber_violations === 0 ? 'Pass' : `${d.veber_violations} violation${d.veber_violations > 1 ? 's' : ''}`, color: d.veber_violations === 0 ? '#16a34a' : '#dc2626' });
  // Safety alerts
  rows.push({ label: 'PAINS', value: d.pains_alerts === 0 ? 'Clean' : `${d.pains_alerts} alert${d.pains_alerts > 1 ? 's' : ''}`, color: d.pains_alerts === 0 ? '#16a34a' : '#dc2626' });
  if (d.pains_descriptions?.length > 0) {
    rows.push({ label: 'PAINS flags', value: d.pains_descriptions.join(', '), color: '#dc2626' });
  }
  rows.push({ label: 'Brenk', value: d.brenk_alerts === 0 ? 'Clean' : `${d.brenk_alerts} alert${d.brenk_alerts > 1 ? 's' : ''}`, color: d.brenk_alerts === 0 ? '#16a34a' : '#d97706' });
  if (d.brenk_descriptions?.length > 0) {
    rows.push({ label: 'Brenk flags', value: d.brenk_descriptions.join(', '), color: '#d97706' });
  }
  // Leadlikeness
  rows.push({ label: 'Lead-like', value: d.leadlikeness_class, color: d.leadlikeness_class === 'Yes' ? '#16a34a' : undefined });
  // Key physicochemical
  rows.push({ label: 'LogP', value: String(d.logp), color: d.logp > 5 ? '#dc2626' : undefined });
  rows.push({ label: 'TPSA', value: `${d.tpsa} \u00c5\u00b2` });
  rows.push({ label: 'Fsp3', value: d.fsp3 != null ? Number(d.fsp3).toFixed(2) : '—' });
  return rows;
};

const fmtToxicophores = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  const verdict = d.clean;
  rows.push({ label: 'Overall', value: verdict ? 'Clean' : `${d.total_alerts} alert${d.total_alerts > 1 ? 's' : ''}`, color: verdict ? '#16a34a' : '#dc2626' });
  // PAINS
  const pains = d.pains || {};
  rows.push({ label: 'PAINS', value: pains.count === 0 ? 'PASS' : `${pains.count} hit${pains.count > 1 ? 's' : ''}`, color: pains.count === 0 ? '#16a34a' : '#dc2626' });
  if (pains.matches?.length > 0) {
    pains.matches.forEach((m: string) => rows.push({ label: 'PAINS', value: m, color: '#dc2626' }));
  }
  // Brenk
  const brenk = d.brenk || {};
  rows.push({ label: 'Brenk', value: brenk.count === 0 ? 'PASS' : `${brenk.count} hit${brenk.count > 1 ? 's' : ''}`, color: brenk.count === 0 ? '#16a34a' : '#d97706' });
  if (brenk.matches?.length > 0) {
    brenk.matches.forEach((m: string) => rows.push({ label: 'Brenk', value: m, color: '#d97706' }));
  }
  // NIH
  const nih = d.nih || {};
  rows.push({ label: 'NIH', value: nih.count === 0 ? 'PASS' : `${nih.count} hit${nih.count > 1 ? 's' : ''}`, color: nih.count === 0 ? '#16a34a' : '#d97706' });
  if (nih.matches?.length > 0) {
    nih.matches.slice(0, 5).forEach((m: string) => rows.push({ label: 'NIH', value: m, color: '#d97706' }));
    if (nih.matches.length > 5) rows.push({ label: '', value: `+ ${nih.matches.length - 5} more` });
  }
  return rows;
};

const fmtCollision = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  const hits = d.neighbors?.length || d.total || 0;
  rows.push({ label: 'Hits', value: String(hits), color: hits > 0 ? '#dc2626' : '#16a34a' });
  if (d.neighbors) {
    d.neighbors.slice(0, 5).forEach((n: any) => {
      rows.push({
        label: `Zone ${n.zone ?? '?'}`,
        value: `${(n.similarity * 100).toFixed(0)}% — ${n.patent_id || n.surechembl_id || ''}`,
      });
    });
    if (d.neighbors.length > 5) {
      rows.push({ label: '', value: `+ ${d.neighbors.length - 5} more` });
    }
  }
  return rows;
};

const fmtSubstructure = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  if (d.amide_n_count != null) rows.push({ label: 'Amide N', value: String(d.amide_n_count) });
  if (d.amine_count != null) rows.push({ label: 'Amines', value: String(d.amine_count) });
  if (d.alkyl_amine_count != null) rows.push({ label: 'Alkyl Amines', value: String(d.alkyl_amine_count) });
  if (d.aryl_amine_count != null) rows.push({ label: 'Aryl Amines', value: String(d.aryl_amine_count) });
  if (d.aromatic_n_count != null) rows.push({ label: 'Aromatic N', value: String(d.aromatic_n_count) });
  if (d.basic_n_count != null) rows.push({ label: 'Basic N', value: String(d.basic_n_count) });
  if (d.acidic_o_count != null) rows.push({ label: 'Acidic O', value: String(d.acidic_o_count) });
  return rows;
};

const fmtKeyProperties = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  if (d.formula != null) rows.push({ label: 'Formula', value: d.formula });
  if (d.mw != null) rows.push({ label: 'MW', value: d.mw.toFixed(2) + ' Da' });
  if (d.logp != null) rows.push({ label: 'LogP', value: d.logp.toFixed(2), color: d.logp > 5 ? '#dc2626' : undefined });
  if (d.tpsa != null) rows.push({ label: 'TPSA', value: d.tpsa.toFixed(1) + ' \u00c5\u00b2' });
  if (d.hba != null) rows.push({ label: 'HBA', value: d.hba + ' (count)' });
  if (d.hbd != null) rows.push({ label: 'HBD', value: d.hbd + ' (count)' });
  if (d.rotatable_bonds != null) rows.push({ label: 'Rotatable Bonds', value: d.rotatable_bonds + ' (count)' });
  if (d.ring_count != null) rows.push({ label: 'Rings', value: String(d.ring_count) });
  if (d.fsp3 != null) rows.push({ label: 'Fsp3', value: d.fsp3.toFixed(3) });
  // Lipinski violations
  let violations = 0;
  if (d.mw != null && d.mw > 500) violations++;
  if (d.logp != null && d.logp > 5) violations++;
  if (d.hbd != null && d.hbd > 5) violations++;
  if (d.hba != null && d.hba > 10) violations++;
  rows.push({ label: 'Lipinski violations', value: String(violations), color: violations === 0 ? '#16a34a' : violations <= 1 ? '#d97706' : '#dc2626' });
  return rows;
};

const fmtExtendedDescriptors = (d: any): ResultRow[] => {
  const descs = d.descriptors || {};
  const entries = Object.entries(descs).sort(([a], [b]) => a.localeCompare(b));
  const rows: ResultRow[] = [
    { label: 'Total descriptors', value: String(entries.length) },
  ];
  for (const [key, val] of entries) {
    const numVal = val as number;
    rows.push({ label: key, value: typeof numVal === 'number' ? (Number.isInteger(numVal) ? String(numVal) : numVal.toFixed(4)) : String(val) });
  }
  return rows;
};

const fmtMetabolismLiability = (d: any): ResultRow[] => {
  const l = d.liability || {};
  const rows: ResultRow[] = [];
  const riskColor = (r: string) => r === 'Low' ? '#16a34a' : r === 'Medium' ? '#d97706' : '#dc2626';
  rows.push({ label: 'Risk Level', value: l.risk_level || '—', color: riskColor(l.risk_level || '') });
  rows.push({ label: 'Liability Score', value: l.overall_score != null ? l.overall_score.toFixed(1) : '—' });
  const spots = l.soft_spots || [];
  rows.push({ label: 'Soft Spots', value: String(spots.length), color: spots.length > 5 ? '#d97706' : undefined });
  // Show top 3 soft spots
  spots.slice(0, 3).forEach((s: any) => {
    rows.push({ label: `Atom ${s.atom_index} (${s.atom_symbol})`, value: s.reasons?.join(', ') || '—' });
  });
  if (spots.length > 3) rows.push({ label: '', value: `+ ${spots.length - 3} more soft spots` });
  // Alerts
  const alerts = l.alerts || [];
  if (alerts.length > 0) {
    alerts.forEach((a: string) => rows.push({ label: 'Alert', value: a, color: '#dc2626' }));
  }
  return rows;
};

const fmtMetabolismPredict = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  const parent = d.parent || {};
  rows.push({ label: 'Parent', value: parent.formula || d.input_smiles || '—' });
  const metabolites = d.metabolites || [];
  rows.push({ label: 'Metabolites', value: String(metabolites.length) });
  metabolites.slice(0, 8).forEach((m: any, i: number) => {
    rows.push({ label: `#${m.rank ?? i + 1} ${m.transformation}`, value: m.smiles || '—' });
    if (m.delta_mass != null) {
      rows.push({ label: '  \u0394 mass', value: `${m.delta_mass > 0 ? '+' : ''}${m.delta_mass.toFixed(3)} Da` });
    }
  });
  if (metabolites.length > 8) rows.push({ label: '', value: `+ ${metabolites.length - 8} more` });
  return rows;
};

const fmtMetabolismSecondary = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  const sec = d.secondary || {};
  const parent = sec.parent || {};
  rows.push({ label: 'Parent', value: parent.formula || d.input_smiles || '—' });
  const metabolites = sec.metabolites || [];
  rows.push({ label: 'Phase II metabolites', value: String(metabolites.length) });
  metabolites.slice(0, 8).forEach((m: any, i: number) => {
    rows.push({ label: `#${m.rank ?? i + 1} ${m.transformation}`, value: m.smiles || '—' });
    if (m.delta_mass != null) {
      rows.push({ label: '  \u0394 mass', value: `${m.delta_mass > 0 ? '+' : ''}${m.delta_mass.toFixed(3)} Da` });
    }
  });
  if (metabolites.length > 8) rows.push({ label: '', value: `+ ${metabolites.length - 8} more` });
  return rows;
};

const fmtMetabolismFull = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  // Prediction section
  const pred = d.prediction || {};
  const metabolites = pred.metabolites || [];
  rows.push({ label: 'Phase I metabolites', value: String(metabolites.length) });
  metabolites.slice(0, 5).forEach((m: any, i: number) => {
    rows.push({ label: `#${m.rank ?? i + 1} ${m.transformation}`, value: m.smiles || '—' });
  });
  if (metabolites.length > 5) rows.push({ label: '', value: `+ ${metabolites.length - 5} more` });
  // Liability section
  const l = d.liability || {};
  const riskColor = (r: string) => r === 'Low' ? '#16a34a' : r === 'Medium' ? '#d97706' : '#dc2626';
  rows.push({ label: 'Risk Level', value: l.risk_level || '—', color: riskColor(l.risk_level || '') });
  rows.push({ label: 'Liability Score', value: l.overall_score != null ? l.overall_score.toFixed(1) : '—' });
  const spots = (l.soft_spots || []).length;
  rows.push({ label: 'Soft Spots', value: String(spots), color: spots > 5 ? '#d97706' : undefined });
  return rows;
};

const fmtCYPDock = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  rows.push({ label: 'Target', value: d.receptor_label || d.receptor_source || '—' });
  const modes = d.modes || [];
  rows.push({ label: 'Binding modes', value: String(modes.length) });
  if (modes.length > 0) {
    rows.push({ label: 'Best affinity', value: `${modes[0].affinity_kcal_mol?.toFixed(2)} kcal/mol`, color: modes[0].affinity_kcal_mol < -7 ? '#16a34a' : modes[0].affinity_kcal_mol < -5 ? '#d97706' : '#dc2626' });
  }
  modes.slice(0, 5).forEach((m: any) => {
    rows.push({ label: `Mode ${m.mode}`, value: `${m.affinity_kcal_mol?.toFixed(2)} kcal/mol` });
  });
  if (modes.length > 5) rows.push({ label: '', value: `+ ${modes.length - 5} more modes` });
  return rows;
};

// ---------------------------------------------------------------------------
// Interpretation generators (plain English + colour)
// ---------------------------------------------------------------------------

const interpretAdmet = (d: any): Interpretation => {
  const parts: string[] = [];
  parts.push(`${d.gi_absorption === 'High' ? 'HIGH' : 'LOW'} GI absorption`);
  parts.push(`${d.bbb_permeant === 'Yes' ? 'crosses' : 'does NOT cross'} the blood-brain barrier`);
  if (d.lipinski_violations === 0) parts.push('passes Lipinski rules');
  else parts.push(`${d.lipinski_violations} Lipinski violation${d.lipinski_violations > 1 ? 's' : ''}`);
  const color = d.lipinski_violations > 1 || d.gi_absorption !== 'High' ? '#d97706' as const : '#16a34a' as const;
  return { text: `This compound shows ${parts.join(', ')}.`, color };
};

const interpretLipinski = (d: any): Interpretation => {
  let v = 0;
  if (d.mw > 500) v++;
  if (d.logp > 5) v++;
  if (d.hbd > 5) v++;
  if (d.hba > 10) v++;
  if (v === 0) return { text: 'Passes all Lipinski rules \u2014 likely orally bioavailable.', color: '#16a34a' };
  if (v === 1) return { text: `One Lipinski violation \u2014 borderline oral bioavailability.`, color: '#d97706' };
  return { text: `${v} Lipinski violations \u2014 poor oral bioavailability expected.`, color: '#dc2626' };
};

const interpretToxicophores = (d: any): Interpretation => {
  const total = d.total_alerts || 0;
  if (total === 0) return { text: 'No structural alerts detected \u2014 clean compound.', color: '#16a34a' };
  const parts: string[] = [];
  if (d.pains?.count > 0) parts.push(`${d.pains.count} PAINS alert${d.pains.count > 1 ? 's' : ''} (may give false positives in HTS assays)`);
  if (d.brenk?.count > 0) parts.push(`${d.brenk.count} Brenk alert${d.brenk.count > 1 ? 's' : ''}`);
  if (d.nih?.count > 0) parts.push(`${d.nih.count} NIH alert${d.nih.count > 1 ? 's' : ''}`);
  return { text: `${parts.join('; ')}. Consider redesigning.`, color: d.pains?.count > 0 ? '#dc2626' : '#d97706' };
};

const interpretMetabolismLiability = (d: any): Interpretation => {
  const l = d.liability || {};
  const spots = l.soft_spots || [];
  const risk = l.risk_level || 'Unknown';
  if (risk === 'Low') return { text: `Low metabolic liability \u2014 ${spots.length} soft spot${spots.length !== 1 ? 's' : ''} identified.`, color: '#16a34a' };
  const topSpot = spots[0];
  const detail = topSpot ? ` \u2014 atom ${topSpot.atom_index} (${topSpot.atom_symbol}) is the primary metabolic soft spot` : '';
  if (risk === 'Medium') return { text: `${spots.length} vulnerable sites identified${detail}.`, color: '#d97706' };
  return { text: `High metabolic liability with ${spots.length} soft spots${detail}.`, color: '#dc2626' };
};

const interpretHomoLumo = (d: any): Interpretation => {
  const gap = d.gap_ev || 0;
  if (gap > 8) return { text: `HOMO-LUMO gap of ${gap.toFixed(1)} eV indicates high chemical stability.`, color: '#16a34a' };
  if (gap > 4) return { text: `HOMO-LUMO gap of ${gap.toFixed(1)} eV \u2014 moderate reactivity.`, color: '#d97706' };
  return { text: `Narrow HOMO-LUMO gap of ${gap.toFixed(1)} eV \u2014 highly reactive molecule.`, color: '#dc2626' };
};

const interpretProperties = (d: any): Interpretation => {
  const issues: string[] = [];
  if (d.logp > 5) issues.push('high lipophilicity \u2014 may have solubility issues');
  if (d.tpsa > 140) issues.push('high TPSA \u2014 poor membrane permeability expected');
  if (d.mw > 500) issues.push('MW > 500 \u2014 beyond typical drug-like range');
  if (d.hbd > 5) issues.push('excess H-bond donors');
  if (issues.length === 0) return { text: 'Physicochemical properties are within drug-like ranges.', color: '#16a34a' };
  return { text: issues.join('; ') + '.', color: issues.length >= 2 ? '#dc2626' : '#d97706' };
};

const interpretMetabolismPredict = (d: any): Interpretation => {
  const n = (d.metabolites || []).length;
  if (n === 0) return { text: 'No predicted metabolites \u2014 metabolically stable.', color: '#16a34a' };
  if (n <= 3) return { text: `${n} predicted Phase I metabolite${n > 1 ? 's' : ''} \u2014 moderate metabolism expected.`, color: '#d97706' };
  return { text: `${n} predicted Phase I metabolites \u2014 extensive oxidative metabolism expected.`, color: '#dc2626' };
};

const interpretMetabolismFull = (d: any): Interpretation => {
  const risk = d.liability?.risk_level || 'Unknown';
  const n = (d.prediction?.metabolites || []).length;
  if (risk === 'Low') return { text: `Low overall metabolic risk with ${n} predicted metabolite${n !== 1 ? 's' : ''}.`, color: '#16a34a' };
  if (risk === 'Medium') return { text: `Moderate metabolic risk \u2014 ${n} metabolites predicted, consider structural optimization.`, color: '#d97706' };
  return { text: `High metabolic risk \u2014 ${n} metabolites predicted, significant clearance expected.`, color: '#dc2626' };
};

const interpretCYPDock = (d: any): Interpretation => {
  const best = d.modes?.[0]?.affinity_kcal_mol;
  if (best == null) return { text: 'No docking results obtained.', color: '#d97706' };
  if (best < -7) return { text: `Strong CYP binding (${best.toFixed(1)} kcal/mol) \u2014 likely a CYP substrate, expect significant metabolism.`, color: '#dc2626' };
  if (best < -5) return { text: `Moderate CYP binding (${best.toFixed(1)} kcal/mol) \u2014 may undergo CYP-mediated metabolism.`, color: '#d97706' };
  return { text: `Weak CYP binding (${best.toFixed(1)} kcal/mol) \u2014 unlikely to be a major CYP substrate.`, color: '#16a34a' };
};

const interpretConformers = (d: any): Interpretation => {
  const n = d.conformers?.length || 0;
  if (n === 0) return { text: 'No conformers generated.', color: '#d97706' };
  const spread = n > 1 ? Math.abs((d.conformers[n - 1]?.energy_kcal_mol || 0) - (d.conformers[0]?.energy_kcal_mol || 0)) : 0;
  if (spread < 2) return { text: `${n} conformers within ${spread.toFixed(1)} kcal/mol \u2014 rigid molecule with limited flexibility.`, color: '#16a34a' };
  return { text: `${n} conformers spanning ${spread.toFixed(1)} kcal/mol \u2014 flexible molecule with multiple accessible shapes.`, color: '#d97706' };
};

// ---------------------------------------------------------------------------
// Formatters for new Molecular Hub / Combi Hub tools
// ---------------------------------------------------------------------------

const fmtFingerprints = (d: any): ResultRow[] => {
  const fps = d.fingerprints || [];
  const rows: ResultRow[] = [{ label: 'Fingerprints', value: `${fps.length} calculated` }];
  fps.forEach((fp: any) => {
    rows.push({ label: fp.name, value: `${fp.onbits} on-bits / ${fp.nbits}` });
  });
  return rows;
};

const fmtConvert = (d: any): ResultRow[] => [
  { label: 'Input', value: d.input_smiles || '—' },
  { label: 'Output', value: d.output_text || '—' },
];

const fmtTopology = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  if (d.heavy_atom_count != null) rows.push({ label: 'Heavy atoms', value: String(d.heavy_atom_count) });
  if (d.hetero_atom_count != null) rows.push({ label: 'Heteroatoms', value: String(d.hetero_atom_count) });
  if (d.aromatic_atom_count != null) rows.push({ label: 'Aromatic atoms', value: String(d.aromatic_atom_count) });
  if (d.total_ring_count != null) rows.push({ label: 'Total rings', value: String(d.total_ring_count) });
  if (d.aromatic_ring_count != null) rows.push({ label: 'Aromatic rings', value: String(d.aromatic_ring_count) });
  if (d.stereo_center_count != null) rows.push({ label: 'Stereo centres', value: String(d.stereo_center_count) });
  if (d.rotatable_bond_count != null) rows.push({ label: 'Rotatable bonds', value: String(d.rotatable_bond_count) });
  const atoms = d.atom_counts || {};
  if (Object.keys(atoms).length > 0) rows.push({ label: 'Atom counts', value: Object.entries(atoms).map(([k, v]) => `${k}:${v}`).join(' ') });
  return rows;
};

const fmtShape = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  if (d.npr1 != null) rows.push({ label: 'NPR1', value: d.npr1.toFixed(3) });
  if (d.npr2 != null) rows.push({ label: 'NPR2', value: d.npr2.toFixed(3) });
  if (d.asphericity != null) rows.push({ label: 'Asphericity', value: d.asphericity.toFixed(3) });
  if (d.eccentricity != null) rows.push({ label: 'Eccentricity', value: d.eccentricity.toFixed(3) });
  if (d.radius_of_gyration != null) rows.push({ label: 'Radius of gyration', value: `${d.radius_of_gyration.toFixed(2)} \u00c5` });
  if (d.globularity_proxy != null) rows.push({ label: 'Globularity', value: d.globularity_proxy.toFixed(3) });
  if (d.labute_asa != null) rows.push({ label: 'Labute ASA', value: `${d.labute_asa.toFixed(1)} \u00c5\u00b2` });
  if (d.mol_volume != null) rows.push({ label: 'Volume', value: `${d.mol_volume.toFixed(1)} \u00c5\u00b3` });
  return rows;
};

const interpretShape = (d: any): Interpretation => {
  const npr1 = d.npr1 ?? 0;
  const npr2 = d.npr2 ?? 0;
  if (npr1 < 0.3 && npr2 > 0.6) return { text: 'Disc-shaped molecule — typical of aromatic planar compounds.', color: '#16a34a' };
  if (npr1 > 0.5 && npr2 > 0.5) return { text: 'Rod-shaped molecule — extended linear topology.', color: '#d97706' };
  return { text: `Globular 3D shape (NPR1=${npr1.toFixed(2)}, NPR2=${npr2.toFixed(2)}).`, color: '#16a34a' };
};

const interpretTopology = (d: any): Interpretation => {
  const stereo = d.stereo_center_count ?? 0;
  const rings = d.total_ring_count ?? 0;
  const aroRings = d.aromatic_ring_count ?? 0;
  const parts: string[] = [];
  if (rings > 0) parts.push(`${rings} ring${rings > 1 ? 's' : ''} (${aroRings} aromatic)`);
  if (stereo > 0) parts.push(`${stereo} stereocentre${stereo > 1 ? 's' : ''}`);
  else parts.push('no stereocentres');
  return { text: parts.join(', ') + '.', color: '#16a34a' };
};

// ---------------------------------------------------------------------------
// Formatters for Name-to-Structure
// ---------------------------------------------------------------------------

const fmtNameToStructure = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  if (d.name) rows.push({ label: 'Name', value: d.name });
  if (d.smiles) rows.push({ label: 'SMILES', value: d.smiles });
  if (d.inchi) rows.push({ label: 'InChI', value: d.inchi });
  if (d.inchi_key) rows.push({ label: 'InChIKey', value: d.inchi_key });
  if (d.stub) rows.push({ label: 'Note', value: d.note || 'Stub mode', color: '#d97706' });
  return rows;
};


// ---------------------------------------------------------------------------
// Formatters for Antibody Hub (stub mode)
// ---------------------------------------------------------------------------

const fmtAntibodyHumanize = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  if (d.stub) rows.push({ label: 'Mode', value: d.note || 'Stub mode', color: '#d97706' });
  rows.push({ label: 'Model', value: d.model || '—' });
  if (d.heavy_chain_length) rows.push({ label: 'Heavy chain', value: `${d.heavy_chain_length} aa` });
  if (d.light_chain_length) rows.push({ label: 'Light chain', value: `${d.light_chain_length} aa` });
  rows.push({ label: 'Humanness (before)', value: d.humanness_score_before?.toFixed(2) || '—' });
  rows.push({ label: 'Humanness (after)', value: d.humanness_score_after?.toFixed(2) || '—', color: '#16a34a' });
  rows.push({ label: 'Mutations suggested', value: String(d.mutations_suggested) });
  rows.push({ label: 'Framework identity', value: d.framework_identity || '—' });
  rows.push({ label: 'CDR preserved', value: d.cdr_preserved ? 'Yes' : 'No', color: d.cdr_preserved ? '#16a34a' : '#dc2626' });
  return rows;
};

const fmtAntibodyOptimize = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  if (d.stub) rows.push({ label: 'Mode', value: d.note || 'Stub mode', color: '#d97706' });
  rows.push({ label: 'Model', value: d.model || '—' });
  rows.push({ label: 'CDR regions', value: `${d.cdr_regions_optimized} optimized` });
  rows.push({ label: 'Predicted KD improvement', value: d.predicted_kd_improvement || '—' });
  rows.push({ label: 'Aggregation risk', value: d.aggregation_risk || '—', color: d.aggregation_risk === 'Low' ? '#16a34a' : '#d97706' });
  (d.variants || []).forEach((v: any) => {
    rows.push({ label: v.name, value: `${v.mutations} mut \u2192 ${v.predicted_affinity_change}` });
  });
  return rows;
};

const fmtAntibodyDevelopability = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [];
  if (d.stub) rows.push({ label: 'Mode', value: d.note || 'Stub mode', color: '#d97706' });
  rows.push({ label: 'Model', value: d.model || '—' });
  const devColor = d.overall_developability === 'Favorable' ? '#16a34a' : '#d97706';
  rows.push({ label: 'Overall', value: d.overall_developability || '—', color: devColor });
  rows.push({ label: 'Aggregation', value: d.aggregation_propensity || '—', color: d.aggregation_propensity === 'Low' ? '#16a34a' : '#d97706' });
  rows.push({ label: 'Viscosity risk', value: d.viscosity_risk || '—' });
  rows.push({ label: 'Immunogenicity', value: d.immunogenicity_score?.toFixed(2) || '—', color: (d.immunogenicity_score || 0) < 0.3 ? '#16a34a' : '#dc2626' });
  rows.push({ label: 'Thermal stability', value: d.thermal_stability || '—' });
  rows.push({ label: 'Charge patches', value: String(d.charge_patches ?? '—') });
  rows.push({ label: 'Hydrophobic patches', value: String(d.hydrophobic_patches ?? '—') });
  if (d.flags?.length > 0) d.flags.forEach((f: string) => rows.push({ label: 'Flag', value: f, color: '#dc2626' }));
  return rows;
};

const interpretAntibodyDevelopability = (d: any): Interpretation => {
  if (d.stub) return { text: `Stub mode \u2014 real developability assessment requires GPU.`, color: '#d97706' };
  if (d.overall_developability === 'Favorable') return { text: 'Favorable developability profile \u2014 low manufacturing risk.', color: '#16a34a' };
  return { text: 'Potential developability concerns \u2014 review aggregation and viscosity.', color: '#d97706' };
};

const fmtMinimize = (d: any): ResultRow[] => {
  const rows: ResultRow[] = [
    { label: 'Force Field', value: d.forcefield || d.method || 'N/A' },
  ];
  if (d.energy != null) rows.push({ label: 'Energy (kJ/mol)', value: typeof d.energy === 'number' ? d.energy.toFixed(2) : String(d.energy) });
  if (d.initial_energy != null) rows.push({ label: 'Initial Energy', value: typeof d.initial_energy === 'number' ? d.initial_energy.toFixed(2) : String(d.initial_energy) });
  if (d.final_energy != null) rows.push({ label: 'Final Energy', value: typeof d.final_energy === 'number' ? d.final_energy.toFixed(2) : String(d.final_energy) });
  if (d.steps != null) rows.push({ label: 'Steps', value: String(d.steps) });
  if (d.converged != null) rows.push({ label: 'Converged', value: d.converged ? 'Yes' : 'No', color: d.converged ? '#16a34a' : '#d97706' });
  if (d.molblock) rows.push({ label: 'Minimised 3D', value: '', molblock: d.molblock });
  return rows;
};


// ---------------------------------------------------------------------------
// MPO Radar Chart inline component
// ---------------------------------------------------------------------------

const ZONE_COLORS: Record<string, string> = { green: '#16a34a', amber: '#d97706', red: '#dc2626' };

const MPORadarDisplay: React.FC<{ data: any; onClose: () => void }> = ({ data, onClose }) => {
  const props = data.properties || {};
  const chartData = Object.entries(props).map(([key, v]: [string, any]) => ({
    property: key,
    score: v.score,
    fullMark: 100,
  }));

  const overall = data.overall_score ?? 0;
  const overallColor = overall >= 80 ? '#16a34a' : overall >= 50 ? '#d97706' : '#dc2626';

  return (
    <div className="mx-2 mb-1 rounded" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', border: '1px solid var(--card-border)' }}>
      <div className="flex items-center justify-between px-2 pt-1">
        <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#0D9488' }}>MPO Score</span>
        <button onClick={onClose} className="p-0.5" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={10} /></button>
      </div>

      {/* Large score number */}
      <div className="text-center py-2">
        <span style={{ fontSize: 36, fontWeight: 800, fontFamily: 'monospace', color: overallColor }}>{overall}</span>
        <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 500 }}>/100</span>
      </div>

      {/* Radar chart */}
      <div style={{ width: '100%', height: 200 }}>
        <RContainer>
          <RChart data={chartData} cx="50%" cy="50%" outerRadius="70%">
            <PGrid stroke="var(--card-border)" />
            <PAngle
              dataKey="property"
              tick={{ fontSize: 10, fill: 'var(--text-secondary)' }}
            />
            <PRadius
              angle={90}
              domain={[0, 100]}
              tick={{ fontSize: 8, fill: 'var(--text-muted)' }}
              tickCount={3}
            />
            <RRadar
              name="Score"
              dataKey="score"
              stroke="#16a34a"
              fill="#16a34a"
              fillOpacity={0.25}
              strokeWidth={2}
            />
          </RChart>
        </RContainer>
      </div>

      {/* Per-property breakdown */}
      <div className="px-2 pb-1">
        {Object.entries(props).map(([key, v]: [string, any]) => (
          <div key={key} className="flex justify-between items-center py-0.5" style={{ borderBottom: '1px solid var(--card-border)' }}>
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{key}</span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono" style={{ color: 'var(--text-primary)' }}>{v.value}</span>
              <span className="text-[10px] font-mono font-bold" style={{ color: ZONE_COLORS[v.zone] || '#a7abb6' }}>{v.score}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Interpretation */}
      {data.interpretation && (
        <div className="mx-2 mb-2 px-2 py-1.5 rounded" style={{
          backgroundColor: `${overallColor}11`,
          borderLeft: `3px solid ${overallColor}`,
        }}>
          <span className="text-[10px]" style={{ color: overallColor, lineHeight: 1.4 }}>
            {data.interpretation}
          </span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared properties caller (reused by multiple tools)
// ---------------------------------------------------------------------------

const propertiesCache: Map<string, any> = new Map();

// Results history cache — persists across compound switches
interface CachedResult {
  rows: ResultRow[];
  raw: any;
  timestamp: number;
}
const resultsHistoryCache: Map<string, Record<string, CachedResult>> = new Map(); // smiles → { toolKey → result }

const fetchProperties = async (smiles: string, signal: AbortSignal) => {
  if (propertiesCache.has(smiles)) return propertiesCache.get(smiles);
  const resp = await apiClient.post('/v2/properties/calculate', { smiles }, { signal });
  propertiesCache.set(smiles, resp.data);
  return resp.data;
};

// ---------------------------------------------------------------------------
// showWhen helpers
// ---------------------------------------------------------------------------

const always = (p: DataProfile) => p.structure;
const hasIC50 = (p: DataProfile) => p.ic50 || p.assay_results;
const hasEC50 = (p: DataProfile) => p.ec50 || p.assay_results;
const hasKinetics = (p: DataProfile) => p.km_vmax;
const hasSPR = (p: DataProfile) => p.spr_data || p.ki;
const hasProtein = (p: DataProfile) => p.protein_sequence;
const hasAntibody = (p: DataProfile) => p.antibody_sequence;

// Entity-type-aware showWhen helpers
const isSmallMolLike = (p: DataProfile) => !p.entity_type || p.entity_type === 'small_molecule' || p.entity_type === 'macrocycle';
const isPeptideOrProtein = (p: DataProfile) => ['peptide', 'protein', 'antibody', 'adc'].includes(p.entity_type || '');
const isAntibodyLike = (p: DataProfile) => ['antibody', 'adc'].includes(p.entity_type || '');
const isNucleicAcid = (p: DataProfile) => ['nucleotide', 'oligonucleotide', 'plasmid'].includes(p.entity_type || '');
const showMolecular = (p: DataProfile) => p.structure && (isSmallMolLike(p) || p.entity_type === 'peptide');
const showAdmet = (p: DataProfile) => p.structure && (isSmallMolLike(p) || p.entity_type === 'peptide');
const showMetabolism = (p: DataProfile) => p.structure && (isSmallMolLike(p) || p.entity_type === 'peptide');
const showLibraryDesign = (p: DataProfile) => p.structure && isSmallMolLike(p);
const showProteomics = (p: DataProfile) => isPeptideOrProtein(p);
const showAntibodyEngineering = (p: DataProfile) => isAntibodyLike(p);

// ---------------------------------------------------------------------------
// Tool definitions — context-aware
// ---------------------------------------------------------------------------

const SECTIONS: ToolSection[] = [
  {
    id: 'molecular',
    label: 'Molecular Analysis',
    icon: <Atom size={14} />,
    showWhen: showMolecular,
    tools: [
      {
        name: 'Conformer Generation', description: '3D conformer ensemble via MMFF/UFF', engine: 'RDKit',
        tooltip: 'Generate 3D molecular shapes to understand how the molecule folds in space',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/conformers/generate', { smiles, num_confs: 5, method: 'mmff' }, { signal });
            return resp.data;
          },
          format: fmtConformers,
          interpret: interpretConformers,
        },
      },
      {
        name: 'HOMO/LUMO Orbitals', description: 'Frontier orbital energies & gap', engine: 'PySCF',
        tooltip: 'Calculate frontier orbital energies to assess reactivity and electronic properties',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/homo-lumo/calculate', { smiles }, { signal });
            return resp.data;
          },
          format: fmtHomoLumo,
          interpret: interpretHomoLumo,
        },
      },
      {
        name: 'IUPAC Naming', description: 'Systematic chemical name from structure', engine: 'STOUT',
        tooltip: 'Convert the molecular structure to its systematic IUPAC chemical name',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/iupac/name', { smiles }, { signal });
            return resp.data;
          },
          format: fmtIupac,
        },
      },
      {
        name: 'Key Properties', description: 'MW, LogP, TPSA, H-bond donors/acceptors', engine: 'RDKit',
        tooltip: 'Calculate essential drug-like properties: MW, LogP, TPSA, hydrogen bonds, and Lipinski violations',
        showWhen: always,
        action: { type: 'api', run: fetchProperties, format: fmtKeyProperties, interpret: interpretProperties },
      },
      {
        name: 'Extended Descriptors', description: 'Full descriptor set for QSAR & ML', engine: 'RDKit',
        tooltip: 'Compute the full set of molecular descriptors for QSAR modelling and machine learning',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/descriptors/extended', { smiles }, { signal });
            return resp.data;
          },
          format: fmtExtendedDescriptors,
        },
      },
      {
        name: 'Topology Descriptors', description: 'Atoms, rings, stereocentres, bond types', engine: 'Molecular Hub',
        tooltip: 'Count atoms, rings, stereocentres, and bond types for structural characterisation',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/props/topology', { smiles }, { signal });
            return resp.data;
          },
          format: fmtTopology,
          interpret: interpretTopology,
        },
      },
      {
        name: '3D Shape Descriptors', description: 'PMI, asphericity, eccentricity, gyration radius', engine: 'Molecular Hub',
        tooltip: 'Compute PMI, asphericity, eccentricity, and radius of gyration from 3D conformation',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/props/shape', { smiles }, { signal });
            return resp.data;
          },
          format: fmtShape,
          interpret: interpretShape,
        },
      },
      {
        name: 'Fingerprints', description: 'Morgan, MACCS & RDKit fingerprints', engine: 'Molecular Hub',
        tooltip: 'Calculate Morgan, MACCS, and other fingerprints for similarity searching',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/fingerprints/calculate', { smiles, which: 'all' }, { signal });
            return resp.data;
          },
          format: fmtFingerprints,
        },
      },
      {
        name: 'Format Converter', description: 'SMILES to InChI, SDF, MOL & more', engine: 'OpenBabel',
        tooltip: 'Convert SMILES to InChI, SDF, MOL, and other chemical file formats',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/convert/format', { smiles, output_format: 'inchi' }, { signal });
            return resp.data;
          },
          format: fmtConvert,
        },
      },
      {
        name: 'Name to Structure', description: 'Drug name → SMILES lookup', engine: 'OPSIN',
        tooltip: 'Convert a drug name (e.g. aspirin, gefitinib) to a SMILES structure',
        showWhen: () => true,
        action: { type: 'name-lookup-inline' } as any,
      },
      {
        name: 'Energy Minimisation', description: 'UFF/MMFF force-field minimisation', engine: 'RDKit',
        tooltip: 'Minimise molecular energy using UFF/MMFF force fields to find lowest-energy conformation',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/minimize', { smiles, forcefield: 'mmff94' }, { signal });
            return resp.data;
          },
          format: fmtMinimize,
        },
      },
    ],
  },
  {
    id: 'admet',
    label: 'ADMET & Safety',
    icon: <Pill size={14} />,
    showWhen: showAdmet,
    tools: [
      {
        name: 'Property Calculator', description: 'Physicochemical properties & counts', engine: 'RDKit',
        tooltip: 'Calculate physicochemical properties including molecular weight, LogP, TPSA, and ring counts',
        showWhen: always,
        action: { type: 'api', run: fetchProperties, format: fmtProperties, interpret: interpretProperties },
      },
      {
        name: 'Lipinski / Veber Rules', description: 'Oral bioavailability rule checks', engine: 'RDKit',
        tooltip: 'Check oral bioavailability rules: molecular weight, LogP, hydrogen bond donors/acceptors',
        showWhen: always,
        action: { type: 'api', run: fetchProperties, format: fmtLipinski, interpret: interpretLipinski },
      },
      {
        name: 'ADMET Prediction', description: 'Druglikeness, absorption, BBB, solubility', engine: 'RDKit',
        tooltip: 'Predict absorption, distribution, metabolism, excretion and toxicity properties',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/admet/predict', { smiles }, { signal });
            return resp.data;
          },
          format: fmtAdmet,
          interpret: interpretAdmet,
        },
      },
      {
        name: 'Toxicophore Alerts', description: 'PAINS, Brenk & NIH structural alerts', engine: 'RDKit',
        tooltip: 'Scan for known problematic substructures: PAINS, Brenk, and NIH structural alerts',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/toxicophores/scan', { smiles }, { signal });
            return resp.data;
          },
          format: fmtToxicophores,
          interpret: interpretToxicophores,
        },
      },
      {
        name: 'ADMET Hub', description: 'Full ADMET prediction & toxicity workflow', engine: 'ADMET Hub',
        tooltip: 'Open the full ADMET Hub: comprehensive in silico ADMET prediction with toxicity, distribution, and metabolism analysis',
        showWhen: always,
        action: { type: 'navigate', path: '/', hubPort: 8112, hubSlug: 'admet' },
      },
    ],
  },
  {
    id: 'metabolism',
    label: 'Metabolism',
    icon: <Beaker size={14} />,
    showWhen: showMetabolism,
    tools: [
      {
        name: 'Metabolic Liability', description: 'Soft-spot identification for metabolic stability', engine: 'Metabolism Hub',
        tooltip: 'Identify positions on the molecule vulnerable to metabolic breakdown',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/metabolism/liability', { smiles }, { signal });
            return resp.data;
          },
          format: fmtMetabolismLiability,
          interpret: interpretMetabolismLiability,
        },
      },
      {
        name: 'Metabolite Prediction', description: 'Phase I CYP-mediated oxidation products', engine: 'Metabolism Hub',
        tooltip: 'Predict primary Phase I metabolites from CYP-mediated oxidation reactions',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/metabolism/predict', { smiles }, { signal });
            return resp.data;
          },
          format: fmtMetabolismPredict,
          interpret: interpretMetabolismPredict,
        },
      },
      {
        name: 'Secondary Metabolism', description: 'Phase II glucuronidation, sulfation, methylation', engine: 'Metabolism Hub',
        tooltip: 'Predict Phase II conjugation metabolites: glucuronidation, sulfation, methylation',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/metabolism/secondary', { smiles }, { signal });
            return resp.data;
          },
          format: fmtMetabolismSecondary,
        },
      },
      {
        name: 'Full Metabolism Analysis', description: 'Combined Phase I + liability report', engine: 'Metabolism Hub',
        tooltip: 'Complete metabolism report: Phase I prediction + liability scoring in one call',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/metabolism/full', { smiles }, { signal });
            return resp.data;
          },
          format: fmtMetabolismFull,
          interpret: interpretMetabolismFull,
        },
      },
      {
        name: 'CYP Docking', description: 'CYP3A4 active-site binding affinity', engine: 'AutoDock Vina',
        tooltip: 'Dock into CYP3A4 active site to predict metabolic binding affinity',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/metabolism/cyp-dock', { smiles, cyp_preset: 'cyp3a4' }, { signal });
            return resp.data;
          },
          format: fmtCYPDock,
          interpret: interpretCYPDock,
        },
      },
      {
        name: 'Metabolism Hub', description: 'SOM prediction & CYP metabolite predictions', engine: 'Metabolism Hub',
        tooltip: 'Open the Metabolism Hub: site-of-metabolism prediction, CYP-specific metabolite predictions, and mechanistic analysis',
        showWhen: always,
        action: { type: 'navigate', path: '/', hubPort: 8089, hubSlug: 'metabolism' },
      },
    ],
  },
  {
    id: 'kinetics',
    label: 'Kinetics & Binding',
    icon: <Activity size={14} />,
    tools: [
      {
        name: 'IC50 Dose-Response', description: '4-parameter logistic curve fitting', engine: 'SciPy',
        tooltip: 'Fit a dose-response curve to determine the concentration that inhibits 50% of activity',
        showWhen: hasIC50,
        action: { type: 'navigate', path: '/workbench/ic50' },
      },
      {
        name: 'EC50 Fitting', description: 'Agonist dose-response curve fitting', engine: 'SciPy',
        tooltip: 'Fit a dose-response curve to determine the concentration producing 50% of maximum effect',
        showWhen: hasEC50,
        action: { type: 'navigate', path: '/workbench/ic50' },
      },
      {
        name: 'Michaelis-Menten', description: 'Km & Vmax enzyme kinetics fitting', engine: 'SciPy',
        tooltip: 'Fit enzyme kinetics data to determine Km and Vmax parameters',
        showWhen: hasKinetics,
        action: { type: 'navigate', path: '/workbench/kinetics' },
      },
      {
        name: 'SPR Sensorgrams', description: 'kon, koff & KD from binding curves', engine: 'SciPy',
        tooltip: 'Fit surface plasmon resonance binding curves to extract kon, koff, and KD',
        showWhen: hasSPR,
        action: { type: 'navigate', path: '/workbench/spr' },
      },
    ],
  },
  {
    id: 'library',
    label: 'Library Design',
    icon: <Layers size={14} />,
    showWhen: showLibraryDesign,
    tools: [
      {
        name: 'PAINS / BRENK / NIH Filters', description: 'Pan-assay interference & motif screening', engine: 'RDKit',
        tooltip: 'Screen for pan-assay interference compounds and known problematic chemical motifs',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/toxicophores/scan', { smiles }, { signal });
            return resp.data;
          },
          format: fmtToxicophores,
        },
      },
      {
        name: 'Library Properties', description: 'Grouped properties for SAR analysis', engine: 'Combi Hub',
        tooltip: 'Calculate grouped physicochemical properties for library design and SAR analysis',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/library/properties', { smiles }, { signal });
            return resp.data;
          },
          format: (d: any): ResultRow[] => {
            const rows: ResultRow[] = [];
            if (d.properties) {
              for (const [group, props] of Object.entries(d.properties as Record<string, any>)) {
                rows.push({ label: group.toUpperCase(), value: '', color: '#0d9488' });
                if (typeof props === 'object' && props !== null) {
                  for (const [k, v] of Object.entries(props as Record<string, any>)) {
                    rows.push({ label: `  ${k}`, value: typeof v === 'number' ? v.toFixed(3) : String(v) });
                  }
                }
              }
            }
            return rows.length ? rows : [{ label: 'Result', value: JSON.stringify(d).slice(0, 120) }];
          },
        },
      },
      {
        name: 'Combinatorial Library', description: 'Generate analogue libraries from this scaffold', engine: 'Combi Hub',
        tooltip: 'Open the Combinatorial Hub to enumerate analogue libraries from a scaffold with R-group placeholders',
        showWhen: always,
        action: { type: 'navigate', path: '/', hubPort: 8083, hubSlug: 'combi' },
      },
    ],
  },
  {
    id: 'drugdesign',
    label: 'Drug Design',
    icon: <Crosshair size={14} />,
    tools: [
      {
        name: 'MPO Score', description: 'Radar chart across 6 drug-likeness axes', engine: 'RDKit',
        tooltip: 'Multi-Parameter Optimization scoring — radar chart of drug-likeness across 6 key properties',
        showWhen: always,
        action: { type: 'mpo-inline' } as any,
      },
    ],
  },
  {
    id: 'patent',
    label: 'Patent & IP',
    icon: <Shield size={14} />,
    tools: [
      {
        name: 'Collision Search', description: '28M patent compounds similarity search', engine: 'SureChEMBL',
        tooltip: 'Search 28M patent compounds for structural similarity to assess IP freedom',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles, signal) => {
            const resp = await apiClient.post('/patents/collision-map/smiles', {
              smiles, name: 'Query', limit: 20, min_similarity: 0.7,
            }, { signal });
            return resp.data;
          },
          format: fmtCollision,
        },
      },
      {
        name: 'Zone Classification', description: 'Patent risk zone classification', engine: 'Tanimoto',
        tooltip: 'Classify the molecule into patent risk zones based on structural similarity to known patents',
        showWhen: always,
        action: {
          type: 'api',
          run: async (smiles, signal) => {
            const resp = await apiClient.post('/patents/collision-map/smiles', {
              smiles, name: 'Query', limit: 5, min_similarity: 0.5,
            }, { signal });
            return resp.data;
          },
          format: (d: any) => {
            const zones: Record<number, number> = {};
            (d.neighbors || []).forEach((n: any) => { zones[n.zone ?? 0] = (zones[n.zone ?? 0] || 0) + 1; });
            const rows: ResultRow[] = [];
            const ZONE_LABELS: Record<number, string> = { 0: 'White space', 1: 'Green', 2: 'Yellow', 3: 'Orange', 4: 'Red' };
            const ZONE_COLORS: Record<number, string> = { 0: '#16a34a', 1: '#16a34a', 2: '#eab308', 3: '#f97316', 4: '#dc2626' };
            for (const [z, count] of Object.entries(zones)) {
              rows.push({ label: ZONE_LABELS[+z] || `Zone ${z}`, value: `${count} hit${count > 1 ? 's' : ''}`, color: ZONE_COLORS[+z] });
            }
            if (rows.length === 0) rows.push({ label: 'Zone', value: 'White space (no hits)', color: '#16a34a' });
            return rows;
          },
        },
      },
    ],
  },
  {
    id: 'calcs',
    label: 'Equation Solver',
    icon: <Calculator size={14} />,
    tools: [
      {
        name: 'Equation Solver', description: 'Symbolic solving across 11 scientific domains', engine: 'SymPy',
        tooltip: 'Solve scientific equations across 11 domains: PK, physical chemistry, formulation, and more',
        showWhen: () => true,
        action: { type: 'calcs-inline' },
      },
    ],
  },
  {
    id: 'pkpd',
    label: 'PK/PD Modelling',
    icon: <Syringe size={14} />,
    tools: [
      {
        name: 'PK Modelling', description: 'IV, oral & steady-state compartmental simulation', engine: 'mrgsolve',
        tooltip: 'Run compartmental pharmacokinetic simulations: IV bolus, oral absorption, steady state',
        showWhen: () => true,
        action: { type: 'navigate', path: '/workbench/pkpd' },
      },
    ],
  },
  {
    id: 'proteomics',
    label: 'Proteomics',
    icon: <Microscope size={14} />,
    showWhen: showProteomics,
    tools: [
      {
        name: 'PTM Prediction', description: 'Post-translational modification sites', engine: 'Proteomics Hub',
        tooltip: 'Predict post-translational modifications from protein sequence (coming soon)',
        showWhen: always,
        action: {
          type: 'api',
          run: async (_smiles: string, _signal: AbortSignal) => {
            return { coming_soon: true, message: 'Proteomics hub is not yet deployed — check back soon.' };
          },
          format: (d: any): ResultRow[] => [{ label: 'Status', value: d.message || 'Coming soon', color: 'var(--text-secondary)' }],
        },
      },
      {
        name: 'Enzymatic Digest', description: 'Sequential protease digest simulation', engine: 'Proteomics Hub',
        tooltip: 'Simulate sequential protease digest of a protein sequence (coming soon)',
        showWhen: always,
        action: {
          type: 'api',
          run: async (_smiles: string, _signal: AbortSignal) => {
            return { coming_soon: true, message: 'Proteomics hub is not yet deployed — check back soon.' };
          },
          format: (d: any): ResultRow[] => [{ label: 'Status', value: d.message || 'Coming soon', color: 'var(--text-secondary)' }],
        },
      },
      {
        name: 'Antibody Mapping', description: 'Epitope & binding region mapping', engine: 'Proteomics Hub',
        tooltip: 'Map antibody epitopes and binding regions (coming soon)',
        showWhen: always,
        action: {
          type: 'api',
          run: async (_smiles: string, _signal: AbortSignal) => {
            return { coming_soon: true, message: 'Proteomics hub is not yet deployed — check back soon.' };
          },
          format: (d: any): ResultRow[] => [{ label: 'Status', value: d.message || 'Coming soon', color: 'var(--text-secondary)' }],
        },
      },
    ],
  },
  {
    id: 'antibody',
    label: 'Antibody Engineering',
    icon: <Shield size={14} />,
    showWhen: showAntibodyEngineering,
    tools: [
      {
        name: 'Humanization', description: 'CDR-preserving sequence humanisation', engine: 'Antibody Hub',
        tooltip: 'Humanize non-human antibody sequences while preserving CDR binding',
        showWhen: hasAntibody,
        action: {
          type: 'api',
          run: async (_smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/antibody/humanize', { heavy_chain: 'PLACEHOLDER', light_chain: 'PLACEHOLDER' }, { signal });
            return resp.data;
          },
          format: fmtAntibodyHumanize,
        },
      },
      {
        name: 'CDR Optimization', description: 'Affinity maturation of CDR loops', engine: 'Antibody Hub',
        tooltip: 'Optimize complementarity-determining regions for improved binding affinity',
        showWhen: hasAntibody,
        action: {
          type: 'api',
          run: async (_smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/antibody/optimize', { heavy_chain: 'PLACEHOLDER', light_chain: 'PLACEHOLDER' }, { signal });
            return resp.data;
          },
          format: fmtAntibodyOptimize,
        },
      },
      {
        name: 'Developability', description: 'Aggregation, viscosity & immunogenicity', engine: 'Antibody Hub',
        tooltip: 'Assess manufacturing viability: aggregation, viscosity, immunogenicity',
        showWhen: hasAntibody,
        action: {
          type: 'api',
          run: async (_smiles: string, signal: AbortSignal) => {
            const resp = await apiClient.post('/v2/antibody/developability', { heavy_chain: 'PLACEHOLDER', light_chain: 'PLACEHOLDER' }, { signal });
            return resp.data;
          },
          format: fmtAntibodyDevelopability,
          interpret: interpretAntibodyDevelopability,
        },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Default profile (fallback when endpoint unavailable)
// ---------------------------------------------------------------------------

const DEFAULT_PROFILE: DataProfile = {
  structure: true,
  bioactivity_data: false,
  ic50: false,
  ec50: false,
  ki: false,
  km_vmax: false,
  spr_data: false,
  target_associations: false,
  reactions: false,
  patent_matches: false,
  assay_results: false,
  pk_data: false,
  metabolism_data: false,
  docking_results: false,
  conformers: false,
  permeability_data: false,
  solubility_data: false,
  plasma_binding: false,
  protein_sequence: false,
  genomic_data: false,
  antibody_sequence: false,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface WorkbenchTabProps {
  smiles: string;
  moleculeId?: number;
  onPropertiesSaved?: () => void;
}

// ---------------------------------------------------------------------------
// Batch Runner component (ADMET + Properties on all compounds)
// ---------------------------------------------------------------------------

type BatchMode = 'admet' | 'properties';

interface BatchResult {
  name: string;
  smiles: string;
  data: any;
  error?: string;
}

type SortDir = 'asc' | 'desc';

const BATCH_ADMET_COLS = [
  { key: 'Drug-like', extract: (d: any) => d.druglike ? 'Yes' : 'No', rag: (d: any) => d.druglike ? 'g' : 'r' },
  { key: 'GI Absorption', extract: (d: any) => d.gi_absorption, rag: (d: any) => d.gi_absorption === 'High' ? 'g' : 'a' },
  { key: 'BBB', extract: (d: any) => d.bbb_permeant, rag: (d: any) => d.bbb_permeant === 'Yes' ? 'g' : 'a' },
  { key: 'Solubility', extract: (d: any) => d.esol_class, rag: (d: any) => d.esol_logs >= -3 ? 'g' : d.esol_logs >= -4 ? 'a' : 'r' },
  { key: 'Lipinski', extract: (d: any) => `${d.lipinski_violations} viol`, rag: (d: any) => d.lipinski_violations === 0 ? 'g' : d.lipinski_violations <= 1 ? 'a' : 'r' },
  { key: 'PAINS', extract: (d: any) => d.pains_alerts === 0 ? 'Clean' : `${d.pains_alerts}`, rag: (d: any) => d.pains_alerts === 0 ? 'g' : 'r' },
  { key: 'Lead-like', extract: (d: any) => d.leadlikeness_class, rag: (d: any) => d.leadlikeness_class === 'Yes' ? 'g' : 'a' },
];

const BATCH_PROPS_COLS = [
  { key: 'MW', extract: (d: any) => d.mw?.toFixed(1), rag: (d: any) => d.mw <= 500 ? 'g' : 'r', sort: (d: any) => d.mw },
  { key: 'LogP', extract: (d: any) => d.logp?.toFixed(2), rag: (d: any) => d.logp <= 5 ? 'g' : 'r', sort: (d: any) => d.logp },
  { key: 'TPSA', extract: (d: any) => d.tpsa?.toFixed(1), rag: (d: any) => d.tpsa <= 140 ? 'g' : 'r', sort: (d: any) => d.tpsa },
  { key: 'HBD', extract: (d: any) => String(d.hbd ?? '—'), rag: (d: any) => (d.hbd ?? 0) <= 5 ? 'g' : 'r', sort: (d: any) => d.hbd },
  { key: 'HBA', extract: (d: any) => String(d.hba ?? '—'), rag: (d: any) => (d.hba ?? 0) <= 10 ? 'g' : 'r', sort: (d: any) => d.hba },
  { key: 'RotBonds', extract: (d: any) => String(d.rotatable_bonds ?? '—'), rag: (d: any) => (d.rotatable_bonds ?? 0) <= 10 ? 'g' : 'a', sort: (d: any) => d.rotatable_bonds },
  { key: 'Rings', extract: (d: any) => String(d.ring_count ?? '—'), rag: () => 'g' as const, sort: (d: any) => d.ring_count },
  { key: 'Fsp3', extract: (d: any) => d.fsp3?.toFixed(2) ?? '—', rag: (d: any) => (d.fsp3 ?? 0) >= 0.25 ? 'g' : 'a', sort: (d: any) => d.fsp3 },
];

const ragColor = (r: string) => r === 'g' ? '#16a34a' : r === 'a' ? '#d97706' : '#dc2626';
const ragBg = (r: string) => r === 'g' ? 'rgba(22,163,106,0.08)' : r === 'a' ? 'rgba(217,119,6,0.08)' : 'rgba(220,38,38,0.08)';

const BatchRunner: React.FC<{ mode: BatchMode; onClose: () => void }> = ({ mode, onClose }) => {
  const molecules = useChemistryStore((s) => s.molecules);
  const [batchResults, setBatchResults] = useState<BatchResult[]>([]);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const abortRef = useRef<AbortController | null>(null);

  const cols = mode === 'admet' ? BATCH_ADMET_COLS : BATCH_PROPS_COLS;

  const runBatch = useCallback(async () => {
    const mols = molecules.filter((m) => m.smiles);
    if (mols.length === 0) return;
    setIsRunning(true);
    setTotal(mols.length);
    setProgress(0);
    setBatchResults([]);
    const controller = new AbortController();
    abortRef.current = controller;

    const results: BatchResult[] = [];
    for (let i = 0; i < mols.length; i++) {
      if (controller.signal.aborted) break;
      const mol = mols[i];
      const name = getMoleculeName(mol.properties) || mol.smiles.slice(0, 30);
      try {
        const endpoint = mode === 'admet' ? '/v2/admet/predict' : '/v2/properties/calculate';
        const resp = await apiClient.post(endpoint, { smiles: mol.smiles }, { signal: controller.signal });
        results.push({ name, smiles: mol.smiles, data: resp.data });
      } catch (err: any) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') break;
        results.push({ name, smiles: mol.smiles, data: null, error: err.message?.slice(0, 60) || 'Failed' });
      }
      setProgress(i + 1);
      setBatchResults([...results]);
    }
    setIsRunning(false);
    abortRef.current = null;
  }, [molecules, mode]);

  const exportCSV = () => {
    const header = ['Compound', 'SMILES', ...cols.map((c) => c.key)].join(',');
    const rows = batchResults
      .filter((r) => r.data)
      .map((r) => [
        `"${r.name.replace(/"/g, '""')}"`,
        `"${r.smiles.replace(/"/g, '""')}"`,
        ...cols.map((c) => `"${(c.extract(r.data) ?? '').toString().replace(/"/g, '""')}"`),
      ].join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-${mode}-${batchResults.length}-compounds.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSort = (colKey: string) => {
    if (sortCol === colKey) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(colKey);
      setSortDir('asc');
    }
  };

  const sortedResults = useMemo(() => {
    if (!sortCol) return batchResults;
    const col = cols.find((c) => c.key === sortCol);
    if (!col) return batchResults;
    const getSortVal = (col as any).sort || col.extract;
    return [...batchResults].sort((a, b) => {
      if (!a.data) return 1;
      if (!b.data) return -1;
      const va = getSortVal(a.data);
      const vb = getSortVal(b.data);
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [batchResults, sortCol, sortDir, cols]);

  return (
    <div
      className="mx-2 mb-2 rounded"
      style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', border: '1px solid var(--card-border)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid var(--card-border)' }}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#0D9488' }}>
            Batch {mode === 'admet' ? 'ADMET' : 'Properties'} — {molecules.length} compounds
          </span>
          {isRunning && (
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              {progress}/{total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isRunning && batchResults.length === 0 && (
            <button
              onClick={runBatch}
              className="text-[10px] font-medium px-2 py-0.5 rounded"
              style={{ background: 'rgba(13,148,136,0.15)', color: '#0D9488', border: 'none', cursor: 'pointer' }}
            >
              Run on all ({molecules.length})
            </button>
          )}
          {isRunning && (
            <button
              onClick={() => abortRef.current?.abort()}
              className="text-[10px] font-medium px-2 py-0.5 rounded"
              style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626', border: 'none', cursor: 'pointer' }}
            >
              Stop
            </button>
          )}
          {batchResults.length > 0 && !isRunning && (
            <>
              <button
                onClick={exportCSV}
                className="text-[10px] font-medium px-2 py-0.5 rounded"
                style={{ background: 'rgba(13,148,136,0.1)', color: '#0D9488', border: 'none', cursor: 'pointer' }}
              >
                Export CSV
              </button>
              <button
                onClick={() => { setBatchResults([]); setProgress(0); }}
                className="text-[10px] font-medium px-2 py-0.5 rounded"
                style={{ background: 'rgba(220,38,38,0.06)', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}
              >
                Clear
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className="p-0.5 rounded transition-colors"
            style={{ color: 'var(--text-muted)', cursor: 'pointer', background: 'none', border: 'none' }}
          >
            <X size={10} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {isRunning && (
        <div style={{ height: 2, background: 'var(--card-border)' }}>
          <div style={{ height: '100%', width: `${(progress / total) * 100}%`, background: '#0D9488', transition: 'width 200ms' }} />
        </div>
      )}

      {/* Results table */}
      {batchResults.length > 0 && (
        <div style={{ maxHeight: 400, overflowY: 'auto', overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'rgba(13, 148, 136, 0.06)' }}>
                <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--card-border)', whiteSpace: 'nowrap' }}>#</th>
                <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--card-border)', whiteSpace: 'nowrap', maxWidth: 120 }}>Compound</th>
                {cols.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{
                      textAlign: 'right', padding: '4px 6px', fontWeight: 600,
                      color: sortCol === col.key ? '#0D9488' : 'var(--text-muted)',
                      borderBottom: '1px solid var(--card-border)', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {col.key} {sortCol === col.key ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--card-border)' }}>
                  <td style={{ padding: '3px 6px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{i + 1}</td>
                  <td style={{ padding: '3px 6px', color: 'var(--text-primary)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.smiles}>
                    {r.name}
                  </td>
                  {r.error ? (
                    <td colSpan={cols.length} style={{ padding: '3px 6px', color: '#dc2626' }}>{r.error}</td>
                  ) : r.data ? (
                    cols.map((col) => {
                      const val = col.extract(r.data);
                      const rag = col.rag(r.data);
                      return (
                        <td
                          key={col.key}
                          style={{
                            padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace',
                            color: ragColor(rag), backgroundColor: ragBg(rag),
                          }}
                        >
                          {val ?? '—'}
                        </td>
                      );
                    })
                  ) : (
                    <td colSpan={cols.length} style={{ padding: '3px 6px', color: 'var(--text-muted)' }}>
                      <Loader2 size={10} className="animate-spin inline" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline Equation Solver component
// ---------------------------------------------------------------------------

interface CalcsEquation {
  id: string;
  name: string;
  domain: string;
  symbolic_form: string;
  meaning: string;
  variables: { symbol: string; name: string; default_unit: string }[];
}

const InlineCalcsSolver: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [equations, setEquations] = useState<CalcsEquation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [selectedEq, setSelectedEq] = useState<CalcsEquation | null>(null);
  const [solveFor, setSolveFor] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [units, setUnits] = useState<Record<string, string>>({});
  const [solving, setSolving] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/v2/calcs/equations')
      .then((r) => { setEquations(r.data); setLoading(false); })
      .catch(() => { setError('Failed to load equations'); setLoading(false); });
  }, []);

  const domains = React.useMemo(() => {
    const map: Record<string, CalcsEquation[]> = {};
    equations.forEach((eq) => { if (!map[eq.domain]) map[eq.domain] = []; map[eq.domain].push(eq); });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [equations]);

  const selectEq = (eq: CalcsEquation) => {
    setSelectedEq(eq);
    setResult(null);
    setError('');
    setSolveFor(eq.variables[0]?.symbol || '');
    const u: Record<string, string> = {};
    eq.variables.forEach((v) => { u[v.symbol] = v.default_unit; });
    setUnits(u);
    setValues({});
  };

  const runSolve = async () => {
    if (!selectedEq || !solveFor) return;
    const knowns = selectedEq.variables
      .filter((v) => v.symbol !== solveFor && values[v.symbol]?.trim())
      .map((v) => ({ symbol: v.symbol, value: parseFloat(values[v.symbol]), unit: units[v.symbol] || v.default_unit }));
    if (knowns.length < selectedEq.variables.length - 1) {
      setError(`Fill in all known variables (need ${selectedEq.variables.length - 1}, got ${knowns.length})`);
      return;
    }
    setSolving(true);
    setError('');
    setResult(null);
    try {
      const resp = await apiClient.post('/v2/calcs/solve', { equation_id: selectedEq.id, solve_for: solveFor, knowns });
      setResult(resp.data);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.detail || err.message || 'Solve failed');
    } finally {
      setSolving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '4px 6px', fontSize: 11, fontFamily: 'monospace',
    background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 4,
    color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
  };

  if (loading) return <div className="px-2 py-3 text-center"><Loader2 size={14} className="animate-spin inline" style={{ color: '#0D9488' }} /> <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Loading equations...</span></div>;

  // Step 1: Pick domain
  if (!selectedDomain) {
    return (
      <div className="mx-2 mb-1 rounded" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', border: '1px solid var(--card-border)' }}>
        <div className="flex items-center justify-between px-2 pt-1">
          <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#0D9488' }}>Select Domain</span>
          <button onClick={onClose} className="p-0.5" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={10} /></button>
        </div>
        <div className="px-1 py-1" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {domains.map(([domain, eqs]) => (
            <button key={domain} onClick={() => setSelectedDomain(domain)}
              style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text-primary)', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0D9488'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--card-border)'; }}
            >
              {domain} <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>({eqs.length})</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Step 2: Pick equation
  if (!selectedEq) {
    const domainEqs = equations.filter((eq) => eq.domain === selectedDomain);
    return (
      <div className="mx-2 mb-1 rounded" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', border: '1px solid var(--card-border)' }}>
        <div className="flex items-center justify-between px-2 pt-1">
          <div className="flex items-center gap-1">
            <button onClick={() => setSelectedDomain(null)} style={{ fontSize: 9, color: '#0D9488', background: 'none', border: 'none', cursor: 'pointer' }}>&larr;</button>
            <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#0D9488' }}>{selectedDomain}</span>
          </div>
          <button onClick={onClose} className="p-0.5" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={10} /></button>
        </div>
        <div className="flex flex-col gap-0.5 px-1 py-1">
          {domainEqs.map((eq) => (
            <button key={eq.id} onClick={() => selectEq(eq)}
              className="text-left px-2 py-1 rounded transition-colors"
              style={{ fontSize: 11, color: 'var(--text-primary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div style={{ fontWeight: 500 }}>{eq.name}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{eq.symbolic_form}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Step 3: Input values + solve
  const inputVars = selectedEq.variables.filter((v) => v.symbol !== solveFor);
  return (
    <div className="mx-2 mb-1 rounded" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', border: '1px solid var(--card-border)' }}>
      <div className="flex items-center justify-between px-2 pt-1">
        <div className="flex items-center gap-1">
          <button onClick={() => { setSelectedEq(null); setResult(null); setError(''); }} style={{ fontSize: 9, color: '#0D9488', background: 'none', border: 'none', cursor: 'pointer' }}>&larr;</button>
          <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#0D9488' }}>{selectedEq.name}</span>
        </div>
        <button onClick={onClose} className="p-0.5" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={10} /></button>
      </div>
      <div className="px-2 py-0.5" style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text-muted)' }}>{selectedEq.symbolic_form}</div>

      {/* Solve-for selector */}
      <div className="flex items-center gap-2 px-2 py-1">
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Solve for</span>
        <select value={solveFor} onChange={(e) => { setSolveFor(e.target.value); setResult(null); }}
          style={{ ...inputStyle, width: 'auto', padding: '2px 4px' }}>
          {selectedEq.variables.map((v) => <option key={v.symbol} value={v.symbol}>{v.symbol} ({v.name})</option>)}
        </select>
      </div>

      {/* Input fields */}
      {inputVars.map((v) => (
        <div key={v.symbol} className="flex items-center gap-1.5 px-2 py-0.5">
          <span style={{ fontSize: 10, color: 'var(--text-secondary)', minWidth: 50, fontFamily: 'monospace' }} title={v.name}>{v.symbol}</span>
          <input type="number" step="any" value={values[v.symbol] || ''} placeholder={v.name}
            onChange={(e) => setValues((prev) => ({ ...prev, [v.symbol]: e.target.value }))}
            style={{ ...inputStyle, flex: 1 }} />
          <input type="text" value={units[v.symbol] || v.default_unit}
            onChange={(e) => setUnits((prev) => ({ ...prev, [v.symbol]: e.target.value }))}
            style={{ ...inputStyle, width: 70, fontSize: 10 }} />
        </div>
      ))}

      {/* Solve button */}
      <div className="px-2 py-1.5">
        <button onClick={runSolve} disabled={solving}
          style={{ fontSize: 10, fontWeight: 600, padding: '4px 12px', borderRadius: 6, border: 'none', cursor: solving ? 'wait' : 'pointer',
            background: 'rgba(13, 148, 136, 0.15)', color: '#0D9488' }}>
          {solving ? 'Solving...' : 'Solve'}
        </button>
      </div>

      {/* Error */}
      {error && <div className="px-2 pb-1" style={{ fontSize: 10, color: '#dc2626' }}>{error}</div>}

      {/* Result */}
      {result && (
        <div className="px-2 pb-2">
          <div style={{ padding: '6px 8px', borderRadius: 6, background: 'rgba(13, 148, 136, 0.08)', border: '1px solid rgba(13, 148, 136, 0.2)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', color: '#0D9488' }}>
              {result.solve_for} = {result.value_sigfig} {result.unit}
            </div>
            {result.working?.symbolic_form && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: 2 }}>{result.working.symbolic_form}</div>
            )}
            {result.warnings?.length > 0 && result.warnings.map((w: string, i: number) => (
              <div key={i} style={{ fontSize: 10, color: '#d97706', marginTop: 2 }}>{w}</div>
            ))}
            {result.assumptions?.length > 0 && (
              <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 4 }}>
                Assumes: {result.assumptions.join('; ')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline Name-to-Structure Lookup component
// ---------------------------------------------------------------------------

const InlineNameLookup: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [svgHtml, setSvgHtml] = useState<string | null>(null);
  const [error, setError] = useState('');

  const runLookup = async () => {
    if (!name.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    setSvgHtml(null);
    try {
      const resp = await apiClient.post('/v2/name-to-structure', { name: name.trim() });
      setResult(resp.data);
      // Fetch 2D depiction if we got a SMILES back
      const smiles = resp.data?.smiles;
      if (smiles) {
        try {
          const renderResp = await apiClient.post('/v2/indigo/render', { structure: smiles, output_format: 'svg' }, { responseType: 'text' });
          const svg = typeof renderResp.data === 'string' ? renderResp.data : renderResp.data?.svg || '';
          if (svg) setSvgHtml(svg);
        } catch {
          // Depiction optional — silently ignore
        }
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '4px 6px', fontSize: 11, fontFamily: 'monospace',
    background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 4,
    color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div className="mx-2 mb-1 rounded" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', border: '1px solid var(--card-border)' }}>
      <div className="flex items-center justify-between px-2 pt-1">
        <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#0D9488' }}>Name to Structure</span>
        <button onClick={onClose} className="p-0.5" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={10} /></button>
      </div>

      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <Search size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runLookup(); }}
          placeholder="e.g. aspirin, gefitinib, ibuprofen..."
          style={{ ...inputStyle, flex: 1 }}
          autoFocus
        />
        <button onClick={runLookup} disabled={loading || !name.trim()}
          style={{ fontSize: 10, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: 'none', cursor: loading ? 'wait' : 'pointer',
            background: 'rgba(13, 148, 136, 0.15)', color: '#0D9488', whiteSpace: 'nowrap' }}>
          {loading ? 'Looking up...' : 'Resolve'}
        </button>
      </div>

      {error && <div className="px-2 pb-1" style={{ fontSize: 10, color: '#dc2626' }}>{error}</div>}

      {result && (
        <div className="px-2 pb-2">
          {svgHtml && (
            <div
              className="mb-1.5 rounded overflow-hidden flex items-center justify-center"
              style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', minHeight: 100, maxHeight: 180 }}
              dangerouslySetInnerHTML={{ __html: svgHtml }}
            />
          )}
          <div style={{ padding: '6px 8px', borderRadius: 6, background: 'rgba(13, 148, 136, 0.08)', border: '1px solid rgba(13, 148, 136, 0.2)' }}>
            {result.smiles && (
              <div style={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: '#0D9488', wordBreak: 'break-all' }}>
                {result.smiles}
              </div>
            )}
            {result.name && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Resolved: {result.name}</div>
            )}
            {result.inchi_key && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1, fontFamily: 'monospace' }}>{result.inchi_key}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FORCE_FIELDS = [
  { value: 'mmff94', label: 'MMFF94' },
  { value: 'mmff94s', label: 'MMFF94s' },
  { value: 'uff', label: 'UFF' },
];

const MINIMIZE_FORCE_FIELDS = [
  { value: 'mmff94', label: 'MMFF94' },
  { value: 'mmff94s', label: 'MMFF94s' },
  { value: 'uff', label: 'UFF' },
];

// ---------------------------------------------------------------------------
// Result copy / download helpers
// ---------------------------------------------------------------------------

const copyResultsToClipboard = (rows: ResultRow[]) => {
  const text = rows.filter((r) => !r.molblock).map((r) => `${r.label}\t${r.value}`).join('\n');
  navigator.clipboard.writeText(text);
};

const downloadResultsCSV = (rows: ResultRow[], toolKey: string, smiles: string) => {
  const toolName = toolKey.split(':').pop() || 'results';
  const compound = smiles.length > 20 ? smiles.slice(0, 20) : smiles;
  const safeName = `${toolName}-${compound}`.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
  const csv = 'Property,Value\n' + rows
    .filter((r) => !r.molblock)
    .map((r) => `"${r.label.replace(/"/g, '""')}","${r.value.replace(/"/g, '""')}"`)
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const WorkbenchTab: React.FC<WorkbenchTabProps> = ({ smiles, moleculeId, onPropertiesSaved }) => {
  const [profile, setProfile] = useState<DataProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['molecular', 'admet']));
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ResultRow[]>>({});
  const [rawData, setRawData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [timestamps, setTimestamps] = useState<Record<string, number>>({});
  const abortRef = useRef<AbortController | null>(null);
  const [conformerFF, setConformerFF] = useState('mmff94');
  const [minimizeFF, setMinimizeFF] = useState('mmff94');
  const [calcsOpen, setCalcsOpen] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [batchOpen, setBatchOpen] = useState<BatchMode | null>(null);
  const allMolecules = useChemistryStore((s) => s.molecules);
  const inFileView = allMolecules.length > 1;
  const [focusedToolIdx, setFocusedToolIdx] = useState(-1);
  const containerRef2 = useRef<HTMLDivElement>(null);
  const [recentTrigger, setRecentTrigger] = useState(0);
  const [saveWizard, setSaveWizard] = useState<{ toolKey: string; toolName: string; rawData: any } | null>(null);
  const currentFile = useChemistryStore((s) => s.currentFile);
  const navProjectName = useNavigationStore((s) => s.projectName);

  // Fetch data profile
  useEffect(() => {
    if (!moleculeId) {
      setProfile({ ...DEFAULT_PROFILE, structure: !!smiles });
      return;
    }

    let cancelled = false;
    setProfileLoading(true);

    apiClient.get(`/molecules/${moleculeId}/data-profile`)
      .then((resp) => {
        if (!cancelled) setProfile({ ...resp.data.has, entity_type: resp.data.entity_type } as DataProfile);
      })
      .catch(() => {
        if (!cancelled) setProfile({ ...DEFAULT_PROFILE, structure: !!smiles });
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => { cancelled = true; };
  }, [moleculeId, smiles]);

  // Restore cached results when compound changes
  const prevSmilesRef = useRef(smiles);
  useEffect(() => {
    // Save current results for previous compound before switching
    if (prevSmilesRef.current && prevSmilesRef.current !== smiles) {
      const prevSmiles = prevSmilesRef.current;
      const toSave: Record<string, CachedResult> = {};
      Object.entries(results).forEach(([key, rows]) => {
        toSave[key] = { rows, raw: rawData[key], timestamp: timestamps[key] || Date.now() };
      });
      if (Object.keys(toSave).length > 0) {
        resultsHistoryCache.set(prevSmiles, { ...resultsHistoryCache.get(prevSmiles), ...toSave });
      }
    }
    prevSmilesRef.current = smiles;

    // Restore cached results for new compound
    const cached = resultsHistoryCache.get(smiles);
    if (cached && Object.keys(cached).length > 0) {
      const restoredResults: Record<string, ResultRow[]> = {};
      const restoredRaw: Record<string, any> = {};
      const restoredTs: Record<string, number> = {};
      Object.entries(cached).forEach(([key, c]) => {
        restoredResults[key] = c.rows;
        restoredRaw[key] = c.raw;
        restoredTs[key] = c.timestamp;
      });
      setResults(restoredResults);
      setRawData(restoredRaw);
      setTimestamps(restoredTs);
      setErrors({});
    } else {
      setResults({});
      setRawData({});
      setTimestamps({});
      setErrors({});
    }
    setCalcsOpen(null);
  }, [smiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute visible sections
  const visibleSections = profile
    ? SECTIONS
        .filter((section) => !section.showWhen || section.showWhen(profile))
        .map((section) => ({
          ...section,
          tools: section.tools.filter((t) => t.showWhen(profile)),
        }))
        .filter((section) => section.tools.length > 0)
    : [];

  const toggleSection = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toolKey = (sectionId: string, toolName: string) => `${sectionId}:${toolName}`;

  const clearResult = useCallback((key: string) => {
    setResults((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setRawData((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setCalcsOpen((prev) => prev === key ? null : prev);
  }, []);

  const handleToolClick = useCallback(async (sectionId: string, tool: WorkbenchTool) => {
    const key = toolKey(sectionId, tool.name);

    // Track recent tool usage in sessionStorage
    try {
      const raw = sessionStorage.getItem('workbench_recent_tools');
      const recent: { sectionId: string; toolName: string }[] = raw ? JSON.parse(raw) : [];
      const filtered = recent.filter(r => !(r.sectionId === sectionId && r.toolName === tool.name));
      filtered.unshift({ sectionId, toolName: tool.name });
      sessionStorage.setItem('workbench_recent_tools', JSON.stringify(filtered.slice(0, 5)));
      setRecentTrigger(t => t + 1);
    } catch {}

    if (tool.action.type === 'navigate') {
      // Pass molecule context via URL params so standalone pages can save back
      const contextParams = new URLSearchParams();
      if (smiles) contextParams.set('smiles', smiles);
      if (moleculeId) contextParams.set('molecule_id', String(moleculeId));
      const fid = currentFile?.id ? Number(currentFile.id) : undefined;
      if (fid) contextParams.set('file_id', String(fid));
      if (currentFile?.projectId) contextParams.set('project_id', String(currentFile.projectId));
      // Pass readable names for breadcrumb display
      if (moleculeId) {
        const mol = allMolecules.find(m => m.id === moleculeId);
        const molName = mol ? getMoleculeName(mol.properties) : undefined;
        if (molName) contextParams.set('molecule_name', molName);
      }
      if (currentFile?.fileName) contextParams.set('file_name', currentFile.fileName);
      // Pass project name for breadcrumb display in workbench
      if (navProjectName) contextParams.set('project_name', navProjectName);
      // Build hub URL:
      //   - if REACT_APP_HUBS_BASE_URL + hubSlug set, use "<base>/<slug>" (della/prod via reverse proxy)
      //   - else if hubPort set, use port-based on current host (Mac local dev)
      //   - else use tool.action.path directly (internal /workbench/* routes)
      const envHubsBase = process.env.REACT_APP_HUBS_BASE_URL;
      const hubBase = tool.action.hubPort
        ? (envHubsBase && tool.action.hubSlug
            ? `${envHubsBase.replace(/\/$/, '')}/${tool.action.hubSlug}`
            : `${window.location.protocol}//${window.location.hostname}:${tool.action.hubPort}`)
        : null;
      const basePath = hubBase
        ? `${hubBase}${tool.action.path || '/'}`
        : tool.action.path;
      const sep = basePath.includes('?') ? '&' : '?';
      const url = contextParams.toString() ? `${basePath}${sep}${contextParams.toString()}` : basePath;
      window.open(url, '_blank');
      return;
    }

    if (tool.action.type === 'calcs-inline' || tool.action.type === 'name-lookup-inline' || tool.action.type === 'mpo-inline') {
      // For MPO, auto-run the API call when opening
      if (tool.action.type === 'mpo-inline' && calcsOpen !== key) {
        setCalcsOpen(key);
        setRunning(key);
        const controller = new AbortController();
        abortRef.current = controller;
        try {
          const resp = await apiClient.post('/v2/mpo/score', { smiles }, { signal: controller.signal });
          setRawData((prev) => ({ ...prev, [key]: resp.data }));
        } catch (err: any) {
          if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
            setErrors((prev) => ({ ...prev, [key]: err.message?.slice(0, 120) || 'MPO scoring failed' }));
          }
        } finally {
          setRunning(null);
          abortRef.current = null;
        }
        return;
      }
      setCalcsOpen((prev) => prev === key ? null : key);
      return;
    }

    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;
    setRunning(key);
    setErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });

    try {
      let data: any;
      // Override conformer tool to inject selected force field
      if (tool.name === 'Conformer Generation') {
        const resp = await apiClient.post('/v2/conformers/generate', { smiles, num_confs: 5, force_field: conformerFF }, { signal: controller.signal });
        data = resp.data;
      } else if (tool.name === 'Energy Minimisation') {
        const resp = await apiClient.post('/v2/minimize', { smiles, forcefield: minimizeFF }, { signal: controller.signal });
        data = resp.data;
      } else {
        data = await tool.action.run(smiles, controller.signal);
      }
      const formatted = tool.action.format(data);
      const now = Date.now();
      setResults((prev) => ({ ...prev, [key]: formatted }));
      setRawData((prev) => ({ ...prev, [key]: data }));
      setTimestamps((prev) => ({ ...prev, [key]: now }));
      // Save to history cache
      const cached = resultsHistoryCache.get(smiles) || {};
      cached[key] = { rows: formatted, raw: data, timestamp: now };
      resultsHistoryCache.set(smiles, cached);
    } catch (err: any) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setErrors((prev) => ({ ...prev, [key]: (err.response?.data?.detail || err.message)?.slice(0, 120) || 'Request failed' }));
      }
    } finally {
      setRunning(null);
      abortRef.current = null;
    }
  }, [smiles, conformerFF, minimizeFF, calcsOpen]);

  // Flat list of all visible tools for keyboard navigation
  const flatTools = useMemo(() => {
    const list: { sectionId: string; tool: WorkbenchTool }[] = [];
    visibleSections.forEach((section) => {
      if (expanded.has(section.id)) {
        section.tools.forEach((tool) => list.push({ sectionId: section.id, tool }));
      }
    });
    return list;
  }, [visibleSections, expanded]);

  // Keyboard navigation
  useEffect(() => {
    const el = containerRef2.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedToolIdx((prev) => Math.min(prev + 1, flatTools.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedToolIdx((prev) => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter' && focusedToolIdx >= 0 && focusedToolIdx < flatTools.length) {
        e.preventDefault();
        const { sectionId, tool } = flatTools[focusedToolIdx];
        handleToolClick(sectionId, tool);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Collapse all results
        setResults({});
        setRawData({});
        setTimestamps({});
        setErrors({});
        setCalcsOpen(null);
        setFocusedToolIdx(-1);
      }
    };
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
  }, [flatTools, focusedToolIdx, handleToolClick]);

  // Generate full HTML report
  const [reportGenerating, setReportGenerating] = useState(false);
  const handleGenerateReport = useCallback(async () => {
    if (!smiles || reportGenerating) return;
    setReportGenerating(true);
    try {
      const [propsRes, admetRes, toxRes, svgRes] = await Promise.all([
        apiClient.post('/v2/properties/calculate', { smiles }).catch(() => ({ data: {} })),
        apiClient.post('/v2/admet/predict', { smiles }).catch(() => ({ data: {} })),
        apiClient.post('/v2/toxicophores/scan', { smiles }).catch(() => ({ data: {} })),
        apiClient.post('/v2/indigo/render', {
          structure: { value: smiles, type: 'RAW' },
          width: '300px', height: '200px',
        }, { responseType: 'text' }).catch(() => ({ data: '' })),
      ]);
      const p = propsRes.data;
      const a = admetRes.data;
      const t = toxRes.data;
      const svg = svgRes.data;
      const name = moleculeId
        ? getMoleculeName(allMolecules.find(m => m.id === moleculeId)?.properties) || smiles.slice(0, 30)
        : smiles.slice(0, 30);

      const ragClass = (val: number, good: [number, number]) => {
        if (val >= good[0] && val <= good[1]) return 'rag-green';
        const diff = val < good[0] ? good[0] - val : val - good[1];
        const range = good[1] - good[0];
        return diff / range > 0.5 ? 'rag-red' : 'rag-amber';
      };

      const propRow = (label: string, val: any, good?: [number, number]) => {
        if (val == null) return '';
        const cls = good && typeof val === 'number' ? ` class="${ragClass(val, good)}"` : '';
        return `<tr><td>${label}</td><td${cls}>${typeof val === 'number' ? val.toFixed(2) : val}</td></tr>`;
      };

      const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><title>Report — ${name}</title>
<style>
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:800px;margin:0 auto;padding:24px;background:#fff;color:#1a1d22}
h1{font-size:20px;margin:0 0 4px}
.subtitle{color:#64748b;font-size:12px;margin-bottom:20px}
.smiles{font-family:monospace;font-size:11px;color:#64748b;word-break:break-all;margin-bottom:16px}
.svg-box{text-align:center;margin-bottom:20px;padding:16px;background:#f9fafb;border-radius:10px;border:1px solid #e5e7eb}
h2{font-size:15px;margin:24px 0 8px;color:#0D9488;border-bottom:2px solid #0D9488;padding-bottom:4px}
table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:12px}
th,td{text-align:left;padding:6px 10px;border-bottom:1px solid #e5e7eb}
th{font-weight:600;color:#64748b;font-size:11px;text-transform:uppercase;letter-spacing:0.05em}
td:nth-child(2){text-align:right;font-family:monospace}
.rag-green{color:#16a34a;font-weight:600}
.rag-amber{color:#d97706;font-weight:600}
.rag-red{color:#dc2626;font-weight:600}
.verdict{display:inline-block;padding:2px 8px;border-radius:4px;font-size:12px;font-weight:600}
.verdict-pass{background:#dcfce7;color:#16a34a}
.verdict-fail{background:#fef2f2;color:#dc2626}
.verdict-warn{background:#fffbeb;color:#d97706}
.footer{margin-top:32px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:10px;color:#94a3b8;text-align:center}
</style></head><body>
<h1>${name}</h1>
<div class="smiles">${smiles}</div>
${svg ? `<div class="svg-box">${svg}</div>` : ''}

<h2>Physicochemical Properties</h2>
<table>
<tr><th>Property</th><th>Value</th></tr>
${propRow('Formula', p.formula)}
${propRow('MW', p.mw, [150, 500])}
${propRow('LogP', p.logp, [-0.4, 5])}
${propRow('TPSA', p.tpsa, [20, 140])}
${propRow('HBD', p.hbd, [0, 5])}
${propRow('HBA', p.hba, [0, 10])}
${propRow('Rotatable Bonds', p.rotatable_bonds, [0, 10])}
${propRow('Rings', p.ring_count)}
${propRow('Fsp3', p.fsp3, [0.25, 1])}
${propRow('Heavy Atoms', p.heavy_atoms)}
${propRow('Formal Charge', p.formal_charge)}
${propRow('Molar Refractivity', p.molar_refractivity)}
</table>

<h2>ADMET Predictions</h2>
<table>
<tr><th>Property</th><th>Value</th></tr>
<tr><td>Drug-like</td><td><span class="verdict ${a.druglike ? 'verdict-pass' : 'verdict-fail'}">${a.druglike ? 'Yes' : 'No'}</span></td></tr>
<tr><td>GI Absorption</td><td class="${a.gi_absorption === 'High' ? 'rag-green' : 'rag-amber'}">${a.gi_absorption || '—'}</td></tr>
<tr><td>BBB Permeant</td><td class="${a.bbb_permeant === 'Yes' ? 'rag-green' : ''}">${a.bbb_permeant || '—'}</td></tr>
<tr><td>Solubility</td><td class="${(a.esol_logs ?? 0) >= -3 ? 'rag-green' : (a.esol_logs ?? 0) >= -4 ? 'rag-amber' : 'rag-red'}">${a.esol_class || '—'} (LogS ${a.esol_logs ?? '—'})</td></tr>
<tr><td>Bioavailability</td><td>${a.bioavailability_score != null ? a.bioavailability_score.toFixed(2) : '—'}</td></tr>
<tr><td>Lipinski</td><td class="${(a.lipinski_violations ?? 0) === 0 ? 'rag-green' : (a.lipinski_violations ?? 0) <= 1 ? 'rag-amber' : 'rag-red'}">${(a.lipinski_violations ?? 0) === 0 ? 'Pass' : `${a.lipinski_violations} violation(s)`}</td></tr>
<tr><td>Veber</td><td class="${(a.veber_violations ?? 0) === 0 ? 'rag-green' : 'rag-red'}">${(a.veber_violations ?? 0) === 0 ? 'Pass' : `${a.veber_violations} violation(s)`}</td></tr>
<tr><td>Lead-like</td><td>${a.leadlikeness_class || '—'}</td></tr>
</table>

<h2>Structural Alerts</h2>
<table>
<tr><th>Filter</th><th>Result</th></tr>
<tr><td>Overall</td><td><span class="verdict ${t.clean ? 'verdict-pass' : 'verdict-fail'}">${t.clean ? 'Clean' : `${t.total_alerts} alert(s)`}</span></td></tr>
<tr><td>PAINS</td><td class="${(t.pains?.count ?? 0) === 0 ? 'rag-green' : 'rag-red'}">${(t.pains?.count ?? 0) === 0 ? 'PASS' : `${t.pains?.count} hit(s): ${(t.pains?.matches || []).join(', ')}`}</td></tr>
<tr><td>Brenk</td><td class="${(t.brenk?.count ?? 0) === 0 ? 'rag-green' : 'rag-amber'}">${(t.brenk?.count ?? 0) === 0 ? 'PASS' : `${t.brenk?.count} hit(s): ${(t.brenk?.matches || []).join(', ')}`}</td></tr>
<tr><td>NIH</td><td class="${(t.nih?.count ?? 0) === 0 ? 'rag-green' : 'rag-amber'}">${(t.nih?.count ?? 0) === 0 ? 'PASS' : `${t.nih?.count} hit(s)`}</td></tr>
</table>

<div class="footer">Generated by Adroit Workbench &middot; ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} ${new Date().toLocaleTimeString()}</div>
</body></html>`;

      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a2 = document.createElement('a');
      a2.href = url;
      a2.download = `report-${name.replace(/[^a-zA-Z0-9_-]/g, '_')}.html`;
      document.body.appendChild(a2);
      a2.click();
      document.body.removeChild(a2);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Report generation failed:', err);
    } finally {
      setReportGenerating(false);
    }
  }, [smiles, moleculeId, allMolecules, reportGenerating]);

  // Loading state
  if (profileLoading) {
    return (
      <div className="flex items-center justify-center py-8 gap-2" style={{ color: 'var(--text-muted)' }}>
        <Loader2 size={16} className="animate-spin" style={{ color: '#0D9488' }} />
        <span className="text-xs">Loading tools...</span>
      </div>
    );
  }

  // No tools available
  if (visibleSections.length === 0) {
    return (
      <div className="text-center py-8 px-4">
        <FlaskConical size={28} className="mx-auto mb-2" style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
        <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>No tools available</p>
        <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
          Upload assay data or run predictions to unlock more tools
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef2} tabIndex={0} style={{ outline: 'none' }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] italic" style={{ color: 'var(--text-muted)', margin: 0 }}>
          Showing {visibleSections.reduce((n, s) => n + s.tools.length, 0)} tools based on available data
          <span style={{ opacity: 0.5 }}> &middot; \u2191\u2193 navigate &middot; Enter run &middot; Esc clear</span>
        </p>
        {smiles && (
          <button
            onClick={handleGenerateReport}
            disabled={reportGenerating}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors"
            style={{
              background: 'rgba(13,148,136,0.1)',
              color: '#0D9488',
              border: 'none',
              cursor: reportGenerating ? 'wait' : 'pointer',
              opacity: reportGenerating ? 0.6 : 1,
              flexShrink: 0,
            }}
            title="Download a full HTML report with structure, properties, ADMET, and alerts"
          >
            {reportGenerating ? <Loader2 size={10} className="animate-spin" /> : <FileDown size={10} />}
            Full Report
          </button>
        )}
      </div>

      {/* Recent tools section */}
      {(() => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        void recentTrigger; // force re-read
        try {
          const raw = sessionStorage.getItem('workbench_recent_tools');
          const recent: { sectionId: string; toolName: string }[] = raw ? JSON.parse(raw) : [];
          if (recent.length === 0 || !profile) return null;
          // Resolve recent tools to actual tool objects, filtered by showWhen
          const recentTools = recent
            .map(r => {
              const section = SECTIONS.find(s => s.id === r.sectionId);
              const tool = section?.tools.find(t => t.name === r.toolName);
              return tool && tool.showWhen(profile) ? { sectionId: r.sectionId, tool } : null;
            })
            .filter(Boolean) as { sectionId: string; tool: WorkbenchTool }[];
          if (recentTools.length === 0) return null;
          return (
            <div className="mb-1">
              <div className="flex items-center gap-1.5 py-2 px-2" style={{ color: '#d97706' }}>
                <Clock size={12} className="flex-shrink-0" style={{ opacity: 0.8 }} />
                <span className="text-xs font-semibold">Recent</span>
                <span className="ml-auto text-[10px] font-mono flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                  {recentTools.length}
                </span>
              </div>
              <div className="ml-[22px]">
                {recentTools.map(({ sectionId, tool }) => {
                  const key = toolKey(sectionId, tool.name);
                  const isRunning = running === key;
                  const isNavigate = tool.action.type === 'navigate';
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between py-1.5 px-2 transition-colors"
                      style={{
                        borderBottom: '1px solid var(--card-border)',
                        cursor: 'pointer',
                      }}
                      title={tool.tooltip}
                      onClick={() => handleToolClick(sectionId, tool)}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <div className="flex flex-col">
                        <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                          {tool.name}
                          {isNavigate && <ExternalLink size={9} style={{ opacity: 0.5 }} />}
                        </span>
                        <span className="text-[9px]" style={{ color: 'var(--text-muted)', lineHeight: 1.2 }}>{tool.description}</span>
                      </div>
                      {isRunning && <Loader2 size={12} className="animate-spin" style={{ color: '#0D9488' }} />}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        } catch { return null; }
      })()}

      {visibleSections.map((section) => {
        const isExpanded = expanded.has(section.id);

        return (
          <div key={section.id} className="mb-1">
            {/* Section header */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center gap-1.5 py-2 px-2 rounded transition-colors"
              style={{ color: isExpanded ? '#0D9488' : 'var(--text-secondary)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <ChevronRight
                size={12}
                className="flex-shrink-0 transition-transform duration-200"
                style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
              />
              <span className="flex-shrink-0" style={{ opacity: 0.8 }}>{section.icon}</span>
              <span className="text-xs font-semibold">{section.label}</span>
              <span className="ml-auto text-[10px] font-mono flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                {section.tools.length}
              </span>
            </button>

            {/* Batch runner for ADMET section */}
            {isExpanded && section.id === 'admet' && inFileView && !batchOpen && (
              <div className="ml-[22px] flex items-center gap-1 px-2 py-1" style={{ borderBottom: '1px solid var(--card-border)' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setBatchOpen('admet'); }}
                  className="text-[10px] font-medium px-2 py-0.5 rounded"
                  style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: 'none', cursor: 'pointer' }}
                >
                  Run ADMET on all ({allMolecules.length})
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setBatchOpen('properties'); }}
                  className="text-[10px] font-medium px-2 py-0.5 rounded"
                  style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: 'none', cursor: 'pointer' }}
                >
                  Run Properties on all ({allMolecules.length})
                </button>
              </div>
            )}
            {isExpanded && section.id === 'admet' && batchOpen && (
              <div className="ml-[22px]">
                <BatchRunner mode={batchOpen} onClose={() => setBatchOpen(null)} />
              </div>
            )}

            {/* Tool rows */}
            {isExpanded && (
              <div className="ml-[22px]">
                {section.tools.map((tool) => {
                  const key = toolKey(section.id, tool.name);
                  const isHovered = hoveredTool === key;
                  const isRunning = running === key;
                  const hasResult = results[key] != null;
                  const hasError = errors[key] != null;
                  const isNavigate = tool.action.type === 'navigate';
                  const isCalcsInline = tool.action.type === 'calcs-inline' || tool.action.type === 'name-lookup-inline' || tool.action.type === 'mpo-inline';
                  const isCalcsOpen = calcsOpen === key;
                  const interpretation = hasResult && tool.action.type === 'api' && tool.action.interpret && rawData[key]
                    ? tool.action.interpret(rawData[key]) : null;
                  const flatIdx = flatTools.findIndex((f) => f.sectionId === section.id && f.tool.name === tool.name);
                  const isFocused = flatIdx === focusedToolIdx;

                  return (
                    <div key={tool.name}>
                      {/* Tool row */}
                      <div
                        className="flex items-center justify-between py-1.5 px-2 transition-colors"
                        style={{
                          borderBottom: hasResult || hasError || isCalcsOpen ? 'none' : '1px solid var(--card-border)',
                          cursor: 'pointer',
                          backgroundColor: isFocused ? 'rgba(13,148,136,0.08)' : isHovered ? 'var(--hover-bg)' : 'transparent',
                          borderLeft: isFocused ? '2px solid #0D9488' : '2px solid transparent',
                        }}
                        title={tool.tooltip + (isNavigate ? '' : ' (Enter to run)')}
                        onClick={() => handleToolClick(section.id, tool)}
                        onMouseEnter={() => setHoveredTool(key)}
                        onMouseLeave={() => setHoveredTool(null)}
                      >
                        <div className="flex flex-col">
                          <span
                            className="text-[11px] flex items-center gap-1"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {tool.name}
                            {isNavigate && (
                              <ExternalLink size={9} style={{ opacity: 0.5 }} />
                            )}
                          </span>
                          <span className="text-[9px]" style={{ color: 'var(--text-muted)', lineHeight: 1.2 }}>{tool.description}</span>
                        </div>

                        {isRunning ? (
                          <Loader2 size={12} className="animate-spin" style={{ color: '#0D9488' }} />
                        ) : (
                          <span
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                            style={{
                              color: '#0D9488',
                              backgroundColor: isHovered ? 'rgba(13, 148, 136, 0.1)' : 'transparent',
                              opacity: isHovered ? 1 : 0,
                            }}
                          >
                            {isNavigate ? 'Open' : isCalcsInline ? (isCalcsOpen ? 'Close' : 'Open') : 'Run'}
                          </span>
                        )}
                      </div>

                      {/* Force field selector for conformer tool */}
                      {tool.name === 'Conformer Generation' && (
                        <div
                          className="flex items-center gap-2 px-2 py-1"
                          style={{ borderBottom: hasResult || hasError ? 'none' : '1px solid var(--card-border)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Force field</span>
                          <select
                            value={conformerFF}
                            onChange={(e) => setConformerFF(e.target.value)}
                            className="text-[10px] rounded px-1.5 py-0.5"
                            style={{
                              background: 'var(--card-bg)',
                              border: '1px solid var(--card-border)',
                              color: 'var(--text-primary)',
                              outline: 'none',
                            }}
                          >
                            {FORCE_FIELDS.map((ff) => (
                              <option key={ff.value} value={ff.value}>{ff.label}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Force field selector for energy minimisation tool */}
                      {tool.name === 'Energy Minimisation' && (
                        <div
                          className="flex items-center gap-2 px-2 py-1"
                          style={{ borderBottom: hasResult || hasError ? 'none' : '1px solid var(--card-border)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Force field</span>
                          <select
                            value={minimizeFF}
                            onChange={(e) => setMinimizeFF(e.target.value)}
                            className="text-[10px] rounded px-1.5 py-0.5"
                            style={{
                              background: 'var(--card-bg)',
                              border: '1px solid var(--card-border)',
                              color: 'var(--text-primary)',
                              outline: 'none',
                            }}
                          >
                            {MINIMIZE_FORCE_FIELDS.map((ff) => (
                              <option key={ff.value} value={ff.value}>{ff.label}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Inline results */}
                      {hasResult && (
                        <div
                          className="mx-2 mb-0 rounded-t"
                          style={{
                            backgroundColor: 'rgba(13, 148, 136, 0.04)',
                            border: '1px solid var(--card-border)',
                            borderBottom: interpretation ? 'none' : undefined,
                            maxHeight: results[key].length > 15 ? 320 : undefined,
                            overflowY: results[key].length > 15 ? 'auto' : undefined,
                          }}
                        >
                          <div className="flex items-center justify-between px-2 pt-1 sticky top-0" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', zIndex: 1 }}>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#0D9488' }}>
                                Results {results[key].length > 15 ? `(${results[key].length})` : ''}
                              </span>
                              {timestamps[key] && (
                                <span className="text-[8px]" style={{ color: 'var(--text-muted)' }}>
                                  {new Date(timestamps[key]).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-0.5">
                              {/* Re-run button */}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleToolClick(section.id, tool); }}
                                className="p-0.5 rounded transition-colors"
                                style={{ color: 'var(--text-muted)' }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#0D9488'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                                title="Re-run"
                              >
                                <RotateCw size={10} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyResultsToClipboard(results[key]);
                                  setCopied(key);
                                  setTimeout(() => setCopied(null), 1500);
                                }}
                                className="p-0.5 rounded transition-colors"
                                style={{ color: copied === key ? '#16a34a' : 'var(--text-muted)' }}
                                onMouseEnter={(e) => { if (copied !== key) e.currentTarget.style.color = 'var(--text-primary)'; }}
                                onMouseLeave={(e) => { if (copied !== key) e.currentTarget.style.color = 'var(--text-muted)'; }}
                                title="Copy to clipboard"
                              >
                                {copied === key ? <Check size={10} /> : <Copy size={10} />}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); downloadResultsCSV(results[key], key, smiles); }}
                                className="p-0.5 rounded transition-colors"
                                style={{ color: 'var(--text-muted)' }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                                title="Download CSV"
                              >
                                <FileDown size={10} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setSaveWizard({ toolKey: key, toolName: tool.name, rawData: rawData[key] }); }}
                                className="p-0.5 rounded transition-colors"
                                style={{ color: 'var(--text-muted)' }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#f59e0b'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                                title="Save to Project"
                              >
                                <Database size={10} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); clearResult(key); }}
                                className="p-0.5 rounded transition-colors"
                                style={{ color: 'var(--text-muted)' }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                                title="Clear"
                              >
                                <X size={10} />
                              </button>
                            </div>
                          </div>
                          {results[key].map((row, i) => (
                            <div key={i}>
                              <div
                                className="flex justify-between items-start py-1 px-2"
                                style={{ borderBottom: (!row.molblock && i < results[key].length - 1) ? '1px solid var(--card-border)' : 'none' }}
                              >
                                <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                                <span
                                  className="text-[10px] font-mono text-right max-w-[200px] truncate"
                                  style={{ color: row.color || 'var(--text-primary)' }}
                                  title={row.value}
                                >
                                  {row.value}
                                </span>
                              </div>
                              {row.molblock && (
                                <div className="px-2 pb-2">
                                  <MolblockViewer molblock={row.molblock} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Docking pose viewer — shows after CYP dock results */}
                      {hasResult && rawData[key]?.pose_excerpt && (
                        <div className="mx-2 mb-0 px-2 pb-2 rounded-b" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', border: '1px solid var(--card-border)', borderTop: 'none' }}>
                          <DockingPoseViewer
                            poseData={rawData[key].pose_excerpt}
                            bestAffinity={rawData[key].best_affinity_kcal_mol ?? rawData[key].modes?.[0]?.affinity_kcal_mol ?? 0}
                            receptor={rawData[key].receptor_label || rawData[key].receptor_source || 'Unknown'}
                          />
                        </div>
                      )}

                      {/* Interpretation bar */}
                      {interpretation && (
                        <div
                          className="mx-2 mb-1 px-2 py-1.5 rounded-b"
                          style={{
                            backgroundColor: `${interpretation.color}11`,
                            borderLeft: `3px solid ${interpretation.color}`,
                            borderRight: `1px solid ${interpretation.color}33`,
                            borderBottom: `1px solid ${interpretation.color}33`,
                          }}
                        >
                          <span className="text-[10px]" style={{ color: interpretation.color, lineHeight: 1.4 }}>
                            {interpretation.text}
                          </span>
                        </div>
                      )}

                      {/* Calcs inline solver / Name lookup */}
                      {isCalcsOpen && tool.action.type === 'calcs-inline' && (
                        <InlineCalcsSolver onClose={() => setCalcsOpen(null)} />
                      )}
                      {isCalcsOpen && tool.action.type === 'name-lookup-inline' && (
                        <InlineNameLookup onClose={() => setCalcsOpen(null)} />
                      )}
                      {isCalcsOpen && tool.action.type === 'mpo-inline' && rawData[key] && (
                        <MPORadarDisplay data={rawData[key]} onClose={() => { setCalcsOpen(null); clearResult(key); }} />
                      )}
                      {isCalcsOpen && tool.action.type === 'mpo-inline' && isRunning && (
                        <div className="mx-2 mb-1 px-2 py-3 text-center rounded" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', border: '1px solid var(--card-border)' }}>
                          <Loader2 size={14} className="animate-spin inline" style={{ color: '#0D9488' }} />
                          <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>Computing MPO score...</span>
                        </div>
                      )}

                      {/* Error */}
                      {hasError && (
                        <div className="mx-2 mb-1 px-2 py-1 rounded" style={{ backgroundColor: 'rgba(220, 38, 38, 0.06)', border: '1px solid rgba(220, 38, 38, 0.15)' }}>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px]" style={{ color: '#dc2626' }}>
                              {errors[key]}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); clearResult(key); }}
                              className="p-0.5"
                              style={{ color: '#dc2626' }}
                            >
                              <X size={10} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {saveWizard && (
        <SaveResultsWizard
          isOpen={!!saveWizard}
          onClose={() => setSaveWizard(null)}
          onSaveComplete={onPropertiesSaved}
          rawData={saveWizard.rawData}
          toolName={saveWizard.toolName}
          toolKey={saveWizard.toolKey}
          smiles={smiles}
          moleculeId={moleculeId}
          fileId={currentFile?.id ? Number(currentFile.id) : undefined}
          projectId={currentFile?.projectId}
        />
      )}
    </div>
  );
};

export { FlaskConical as WorkbenchIcon };
export default WorkbenchTab;
